/* eslint-disable @stylistic/max-len */
/**
 * Search
 *
 * Code for searching for dex information, used by the Dex and
 * Teambuilder.
 *
 * Dependencies: battledata, search-index
 * Optional dependencies: pokedex, moves, items, abilities
 *
 * @author Guangcong Luo <guangcongluo@gmail.com>
 * @license MIT
 */

import { Dex, type ModdedDex, toID, type ID } from "./battle-dex";

type SearchType = (
	'pokemon' | 'type' | 'tier' | 'move' | 'item' | 'ability' | 'egggroup' | 'category' | 'article'
);

export type SearchRow = (
	[SearchType, ID, number?, number?] | ['sortpokemon' | 'sortmove', ''] | ['header' | 'html', string]
);

type SearchFilter = [string, string];

/** ID, SearchType, index (if alias), offset (if offset alias) */
declare const BattleSearchIndex: [ID, SearchType, number?, number?][];
declare const BattleSearchIndexOffset: any;
declare const BattleTeambuilderTable: any;

/**
 * Backend for search UIs.
 */
export class DexSearch {
	query = '';

	/**
	 * Dex for the mod/generation to search.
	 */
	dex: ModdedDex = Dex;

	typedSearch: BattleTypedSearch<SearchType> | null = null;

	results: SearchRow[] | null = null;
	exactMatch = false;

	static typeTable = {
		pokemon: 1,
		type: 2,
		tier: 3,
		move: 4,
		item: 5,
		ability: 6,
		egggroup: 7,
		category: 8,
		article: 9,
	};
	static typeName = {
		pokemon: 'Pok&eacute;mon',
		type: 'Type',
		tier: 'Tiers',
		move: 'Moves',
		item: 'Items',
		ability: 'Abilities',
		egggroup: 'Egg group',
		category: 'Category',
		article: 'Article',
	};
	firstPokemonColumn: 'Tier' | 'Number' = 'Number';

	/**
	 * Column to sort by. Default is `null`, a smart sort determined by how good
	 * things are according to the base filters, falling back to dex number (for
	 * Pokemon) and name (for everything else).
	 */
	sortCol: string | null = null;
	reverseSort = false;

	/**
	 * Filters for the search result. Does not include the two base filters
	 * (format and species).
	 */
	filters: SearchFilter[] | null = null;

	constructor(searchType: SearchType | '' = '', formatid = '' as ID, species = '' as ID) {
		this.setType(searchType, formatid, species);
	}

	getTypedSearch(searchType: SearchType | '', format = '' as ID, speciesOrSet: ID | Dex.PokemonSet = '' as ID) {
		if (!searchType) return null;
		switch (searchType) {
		case 'pokemon': return new BattlePokemonSearch('pokemon', format, speciesOrSet);
		case 'item': return new BattleItemSearch('item', format, speciesOrSet);
		case 'move': return new BattleMoveSearch('move', format, speciesOrSet);
		case 'ability': return new BattleAbilitySearch('ability', format, speciesOrSet);
		case 'type': return new BattleTypeSearch('type', format, speciesOrSet);
		case 'category': return new BattleCategorySearch('category', format, speciesOrSet);
		}
		return null;
	}

	find(query: string) {
		query = toID(query);
		if (this.query === query && this.results) {
			return false;
		}
		this.query = query;
		if (!query) {
			this.results = this.typedSearch?.getResults(this.filters, this.sortCol, this.reverseSort) || [];
		} else {
			this.results = this.textSearch(query);
		}
		return true;
	}

	setType(searchType: SearchType | '', format = '' as ID, speciesOrSet: ID | Dex.PokemonSet = '' as ID) {
		// invalidate caches
		this.results = null;

		if (searchType !== this.typedSearch?.searchType) {
			this.filters = null;
			this.sortCol = null;
		}
		this.typedSearch = this.getTypedSearch(searchType, format, speciesOrSet);
		if (this.typedSearch) this.dex = this.typedSearch.dex;
	}

	addFilter(entry: SearchFilter): boolean {
		if (!this.typedSearch) return false;
		let [type] = entry;
		if (this.typedSearch.searchType === 'pokemon') {
			if (type === this.sortCol) this.sortCol = null;
			if (!['type', 'move', 'ability', 'egggroup', 'tier'].includes(type)) return false;
			if (type === 'move') entry[1] = toID(entry[1]);
			if (!this.filters) this.filters = [];
			this.results = null;
			for (const filter of this.filters) {
				if (filter[0] === type && filter[1] === entry[1]) {
					return true;
				}
			}
			this.filters.push(entry);
			return true;
		} else if (this.typedSearch.searchType === 'move') {
			if (type === this.sortCol) this.sortCol = null;
			if (!['type', 'category', 'pokemon'].includes(type)) return false;
			if (type === 'pokemon') entry[1] = toID(entry[1]);
			if (!this.filters) this.filters = [];
			this.filters.push(entry);
			this.results = null;
			return true;
		}
		return false;
	}

	removeFilter(entry?: SearchFilter): boolean {
		if (!this.filters) return false;
		if (entry) {
			const filterid = entry.join(':');
			let deleted: string[] | null = null;
			// delete specific filter
			for (let i = 0; i < this.filters.length; i++) {
				if (filterid === this.filters[i].join(':')) {
					deleted = this.filters[i];
					this.filters.splice(i, 1);
					break;
				}
			}
			if (!deleted) return false;
		} else {
			this.filters.pop();
		}
		if (!this.filters.length) this.filters = null;
		this.results = null;
		return true;
	}

	toggleSort(sortCol: string) {
		if (this.sortCol === sortCol) {
			if (!this.reverseSort) {
				this.reverseSort = true;
			} else {
				this.sortCol = null;
				this.reverseSort = false;
			}
		} else {
			this.sortCol = sortCol;
			this.reverseSort = false;
		}
		this.results = null;
	}

	filterLabel(filterType: string) {
		if (this.typedSearch && this.typedSearch.searchType !== filterType) {
			return 'Filter';
		}
		return null;
	}
	illegalLabel(id: ID) {
		return this.typedSearch?.illegalReasons?.[id] || null;
	}

	getTier(species: Dex.Species) {
		return this.typedSearch?.getTier(species) || '';
	}

	textSearch(query: string): SearchRow[] {
		query = toID(query);

		this.exactMatch = false;
		let searchType: SearchType | '' = this.typedSearch?.searchType || '';

		// If searchType exists, we're searching mainly for results of that type.
		// We'll still search for results of other types, but those results
		// will only be used to filter results for that type.
		let searchTypeIndex = (searchType ? DexSearch.typeTable[searchType] : -1);

		/** searching for "Psychic type" will make the type come up over the move */
		let qFilterType: 'type' | '' = '';
		if (query.endsWith('type')) {
			if (query.slice(0, -4) in window.BattleTypeChart) {
				query = query.slice(0, -4);
				qFilterType = 'type';
			}
		}

		// i represents the location of the search index we're looking at
		let i = DexSearch.getClosest(query);
		this.exactMatch = (BattleSearchIndex[i][0] === query);

		// Even with output buffer buckets, we make multiple passes through
		// the search index. searchPasses is a queue of which pass we're on:
		// [passType, i, query]

		// By doing an alias pass after the normal pass, we ensure that
		// mid-word matches only display after start matches.
		let passType: SearchPassType | '' = '';
		/**
		 * pass types:
		 * * '': time to pop the next pass off the searchPasses queue
		 * * 'normal': start at i and stop when results no longer start with query
		 * * 'alias': like normal, but output aliases instead of non-alias results
		 * * 'fuzzy': start at i and stop when you have two results
		 * * 'exact': like normal, but stop at i
		 */
		type SearchPassType = 'normal' | 'alias' | 'fuzzy' | 'exact';
		/**
		 * [passType, i, query]
		 *
		 * i = index of BattleSearchIndex to start from
		 *
		 * By doing an alias pass after the normal pass, we ensure that
		 * mid-word matches only display after start matches.
		 */
		type SearchPass = [SearchPassType, number, string];
		let searchPasses: SearchPass[] = [['normal', i, query]];

		// For performance reasons, only do an alias pass if query is at
		// least 2 chars long
		if (query.length > 1) searchPasses.push(['alias', i, query]);

		// If the query matches an official alias in BattleAliases: These are
		// different from the aliases in the search index and are given
		// higher priority. We'll do a normal pass through the index with
		// the alias text before any other passes.
		let queryAlias;
		if (query in BattleAliases) {
			if (['sub', 'tr'].includes(query) || !toID(BattleAliases[query]).startsWith(query)) {
				queryAlias = toID(BattleAliases[query]);
				let aliasPassType: SearchPassType = (queryAlias === 'hiddenpower' ? 'exact' : 'normal');
				searchPasses.unshift([aliasPassType, DexSearch.getClosest(queryAlias), queryAlias]);
			}
			this.exactMatch = true;
		}

		// If there are no matches starting with query: Do a fuzzy match pass
		// Fuzzy matches will still be shown after alias matches
		if (!this.exactMatch && BattleSearchIndex[i][0].substr(0, query.length) !== query) {
			// No results start with this. Do a fuzzy match pass.
			let matchLength = query.length - 1;
			if (!i) i++;
			while (matchLength &&
				BattleSearchIndex[i][0].substr(0, matchLength) !== query.substr(0, matchLength) &&
				BattleSearchIndex[i - 1][0].substr(0, matchLength) !== query.substr(0, matchLength)) {
				matchLength--;
			}
			let matchQuery = query.substr(0, matchLength);
			while (i >= 1 && BattleSearchIndex[i - 1][0].substr(0, matchLength) === matchQuery) i--;
			searchPasses.push(['fuzzy', i, '']);
		}

		// We split the output buffers into 8 buckets.
		// Bucket 0 is usually unused, and buckets 1-7 represent
		// pokemon, types, moves, etc (see typeTable).

		// When we're done, the buffers are concatenated together to form
		// our results, with each buffer getting its own header, unlike
		// multiple-pass results, which have no header.

		// Notes:
		// - if we have a searchType, that searchType's buffer will be on top
		let bufs: SearchRow[][] = [[], [], [], [], [], [], [], [], [], []];
		let topbufIndex = -1;

		let count = 0;
		let nearMatch = false;

		/** [type, id, typeIndex] */
		let instafilter: [SearchType, ID, number] | null = null;
		let instafilterSort = [0, 1, 2, 5, 4, 3, 6, 7, 8];
		let illegal = this.typedSearch?.illegalReasons;

		// We aren't actually looping through the entirety of the searchIndex
		for (i = 0; i < BattleSearchIndex.length; i++) {
			if (!passType) {
				let searchPass = searchPasses.shift();
				if (!searchPass) break;
				passType = searchPass[0];
				i = searchPass[1];
				query = searchPass[2];
			}

			let entry = BattleSearchIndex[i];
			let id = entry[0];
			let type = entry[1];

			if (!id) break;

			if (passType === 'fuzzy') {
				// fuzzy match pass; stop after 2 results
				if (count >= 2) {
					passType = '';
					continue;
				}
				nearMatch = true;
			} else if (passType === 'exact') {
				// exact pass; stop after 1 result
				if (count >= 1) {
					passType = '';
					continue;
				}
			} else if (id.substr(0, query.length) !== query) {
				// regular pass, time to move onto our next match
				passType = '';
				continue;
			}

			if (entry.length > 2) {
				// alias entry
				if (passType !== 'alias') continue;
			} else {
				// normal entry
				if (passType === 'alias') continue;
			}

			let typeIndex = DexSearch.typeTable[type];

			// For performance, with a query length of 1, we only fill the first bucket
			if (query.length === 1 && typeIndex !== (searchType ? searchTypeIndex : 1)) continue;

			// For pokemon queries, accept types/tier/abilities/moves/eggroups as filters
			if (searchType === 'pokemon' && (typeIndex === 5 || typeIndex > 7)) continue;
			// For move queries, accept types/categories as filters
			if (searchType === 'move' && ((typeIndex !== 8 && typeIndex > 4) || typeIndex === 3)) continue;
			// For move queries in the teambuilder, don't accept pokemon as filters
			if (searchType === 'move' && illegal && typeIndex === 1) continue;
			// For ability/item queries, don't accept anything else as a filter
			if ((searchType === 'ability' || searchType === 'item') && typeIndex !== searchTypeIndex) continue;
			// Query was a type name followed 'type'; only show types
			if (qFilterType === 'type' && typeIndex !== 2) continue;
			// hardcode cases of duplicate non-consecutive aliases
			if ((id === 'megax' || id === 'megay') && 'mega'.startsWith(query)) continue;

			let matchStart = 0;
			let matchEnd = 0;
			if (passType === 'alias') {
				// alias entry
				// [aliasid, type, originalid, matchStart, originalindex]
				matchStart = entry[3]!;
				let originalIndex = entry[2]!;
				if (matchStart) {
					matchEnd = matchStart + query.length;
					matchStart += (BattleSearchIndexOffset[originalIndex][matchStart] || '0').charCodeAt(0) - 48;
					matchEnd += (BattleSearchIndexOffset[originalIndex][matchEnd - 1] || '0').charCodeAt(0) - 48;
				}
				id = BattleSearchIndex[originalIndex][0];
			} else {
				matchEnd = query.length;
				if (matchEnd) matchEnd += (BattleSearchIndexOffset[i][matchEnd - 1] || '0').charCodeAt(0) - 48;
			}

			// some aliases are substrings
			if (queryAlias === id && query !== id) continue;

			if (searchType && searchTypeIndex !== typeIndex) {
				// This is a filter, set it as an instafilter candidate
				if (!instafilter || instafilterSort[typeIndex] < instafilterSort[instafilter[2]]) {
					instafilter = [type, id, typeIndex];
				}
			}

			// show types above Arceus formes
			if (topbufIndex < 0 && searchTypeIndex < 2 && passType === 'alias' && !bufs[1].length && bufs[2].length) {
				topbufIndex = 2;
			}

			if (illegal && typeIndex === searchTypeIndex) {
				// Always show illegal results under legal results.
				// This is done by putting legal results (and the type header)
				// in bucket 0, and illegal results in the searchType's bucket.
				// searchType buckets are always on top (but under bucket 0), so
				// illegal results will be seamlessly right under legal results.
				if (!bufs[typeIndex].length && !bufs[0].length) {
					bufs[0] = [['header', DexSearch.typeName[type]]];
				}
				if (!(id in illegal)) typeIndex = 0;
			} else {
				if (!bufs[typeIndex].length) {
					bufs[typeIndex] = [['header', DexSearch.typeName[type]]];
				}
			}

			// don't match duplicate aliases
			let curBufLength = (passType === 'alias' && bufs[typeIndex].length);
			if (curBufLength && bufs[typeIndex][curBufLength - 1][1] === id) continue;

			bufs[typeIndex].push([type, id, matchStart, matchEnd]);

			count++;
		}

		let topbuf: SearchRow[] = [];
		if (nearMatch) {
			topbuf = [['html', `<em>No exact match found. The closest matches alphabetically are:</em>`]];
		}
		if (topbufIndex >= 0) {
			topbuf = topbuf.concat(bufs[topbufIndex]);
			bufs[topbufIndex] = [];
		}
		if (searchTypeIndex >= 0) {
			topbuf = topbuf.concat(bufs[0]);
			topbuf = topbuf.concat(bufs[searchTypeIndex]);
			bufs[searchTypeIndex] = [];
			bufs[0] = [];
		}

		if (instafilter && count < 20) {
			// Result count is less than 20, so we can instafilter
			bufs.push(this.instafilter(searchType, instafilter[0], instafilter[1]));
		}

		this.results = Array.prototype.concat.apply(topbuf, bufs);
		return this.results;
	}
	private instafilter(searchType: SearchType | '', fType: SearchType, fId: ID): SearchRow[] {
		let buf: SearchRow[] = [];
		let illegalBuf: SearchRow[] = [];
		let illegal = this.typedSearch?.illegalReasons;
		if (searchType === 'pokemon') {
			switch (fType) {
			case 'type':
				let type = fId.charAt(0).toUpperCase() + fId.slice(1) as Dex.TypeName;
				buf.push(['header', `${type}-type Pok&eacute;mon`]);
				for (let id in BattlePokedex) {
					if (!BattlePokedex[id].types) continue;
					if (this.dex.species.get(id).types.includes(type)) {
						(illegal && id in illegal ? illegalBuf : buf).push(['pokemon', id as ID]);
					}
				}
				break;
			case 'ability':
				let ability = Dex.abilities.get(fId).name;
				buf.push(['header', `${ability} Pok&eacute;mon`]);
				for (let id in BattlePokedex) {
					if (!BattlePokedex[id].abilities) continue;
					if (Dex.hasAbility(this.dex.species.get(id), ability)) {
						(illegal && id in illegal ? illegalBuf : buf).push(['pokemon', id as ID]);
					}
				}
				break;
			}
		} else if (searchType === 'move') {
			switch (fType) {
			case 'type':
				let type = fId.charAt(0).toUpperCase() + fId.slice(1);
				buf.push(['header', `${type}-type moves`]);
				for (let id in BattleMovedex) {
					if (BattleMovedex[id].type === type) {
						(illegal && id in illegal ? illegalBuf : buf).push(['move', id as ID]);
					}
				}
				break;
			case 'category':
				let category = fId.charAt(0).toUpperCase() + fId.slice(1);
				buf.push(['header', `${category} moves`]);
				for (let id in BattleMovedex) {
					if (BattleMovedex[id].category === category) {
						(illegal && id in illegal ? illegalBuf : buf).push(['move', id as ID]);
					}
				}
				break;
			}
		}
		return [...buf, ...illegalBuf];
	}

	static getClosest(query: string) {
		// binary search through the index!
		let left = 0;
		let right = BattleSearchIndex.length - 1;
		while (right > left) {
			let mid = Math.floor((right - left) / 2 + left);
			if (BattleSearchIndex[mid][0] === query && (mid === 0 || BattleSearchIndex[mid - 1][0] !== query)) {
				// that's us
				return mid;
			} else if (BattleSearchIndex[mid][0] < query) {
				left = mid + 1;
			} else {
				right = mid - 1;
			}
		}
		if (left >= BattleSearchIndex.length - 1) left = BattleSearchIndex.length - 1;
		else if (BattleSearchIndex[left + 1][0] && BattleSearchIndex[left][0] < query) left++;
		if (left && BattleSearchIndex[left - 1][0] === query) left--;
		return left;
	}
}

abstract class BattleTypedSearch<T extends SearchType> {
	searchType: T;
	/**
	 * Dex for the mod/generation to search.
	 */
	dex: ModdedDex = Dex;
	/**
	 * Format is the first of two base filters. It constrains results to things
	 * legal in the format, and affects the default sort.
	 *
	 * This string specifically normalizes out generation number and the words
	 * "Doubles" and "Let's Go" from the name.
	 */
	format = '' as ID;
	/**
	 * `species` is the second of two base filters. It constrains results to
	 * things that species can use, and affects the default sort.
	 */
	species = '' as ID;
	/**
	 * `set` is a pseudo-base filter; it has minor effects on move sorting.
	 * (Abilities/items can affect what moves are sorted as usable.)
	 */
	set: Dex.PokemonSet | null = null;

	protected formatType: 'doubles' | 'bdsp' | 'bdspdoubles' | 'bw1' | 'letsgo' | 'metronome' | 'natdex' | 'nfe' |
		'ssdlc1' | 'ssdlc1doubles' | 'predlc' | 'predlcdoubles' | 'predlcnatdex' | 'svdlc1' | 'svdlc1doubles' |
		'svdlc1natdex' | 'stadium' | 'lc' | null = null;

	/**
	 * Cached copy of what the results list would be with only base filters
	 * (i.e. with an empty `query` and `filters`)
	 */
	baseResults: SearchRow[] | null = null;
	/**
	 * Cached copy of all results not in `baseResults` - mostly in case a user
	 * is wondering why a specific result isn't showing up.
	 */
	baseIllegalResults: SearchRow[] | null = null;
	illegalReasons: { [id: string]: string } | null = null;
	results: SearchRow[] | null = null;

	protected readonly sortRow: SearchRow | null = null;

	constructor(searchType: T, format = '' as ID, speciesOrSet: ID | Dex.PokemonSet = '' as ID) {
		this.searchType = searchType;

		this.baseResults = null;
		this.baseIllegalResults = null;

		if (format.startsWith('gen')) {
			const gen = (Number(format.charAt(3)) || 6);
			format = (format.slice(4) || 'customgame') as ID;
			this.dex = Dex.forGen(gen);
		} else if (!format) {
			this.dex = Dex;
		}

		if (format.startsWith('dlc1') && this.dex.gen === 8) {
			if (format.includes('doubles')) {
				this.formatType = 'ssdlc1doubles';
			} else {
				this.formatType = 'ssdlc1';
			}
			format = format.slice(4) as ID;
		}
		if (format.startsWith('predlc')) {
			if (format.includes('doubles') && !format.includes('nationaldex')) {
				this.formatType = 'predlcdoubles';
			} else if (format.includes('nationaldex')) {
				this.formatType = 'predlcnatdex';
			} else {
				this.formatType = 'predlc';
			}
			format = format.slice(6) as ID;
		}
		if (format.startsWith('dlc1') && this.dex.gen === 9) {
			if (format.includes('doubles') && !format.includes('nationaldex')) {
				this.formatType = 'svdlc1doubles';
			} else if (format.includes('nationaldex')) {
				this.formatType = 'svdlc1natdex';
			} else {
				this.formatType = 'svdlc1';
			}
			format = format.slice(4) as ID;
		}
		if (format.startsWith('stadium')) {
			this.formatType = 'stadium';
			format = format.slice(7) as ID;
			if (!format) format = 'ou' as ID;
		}
		if (format.startsWith('vgc')) this.formatType = 'doubles';
		if (format === 'vgc2020') this.formatType = 'ssdlc1doubles';
		if (format === 'vgc2023regulationd') this.formatType = 'predlcdoubles';
		if (format === 'vgc2023regulatione') this.formatType = 'svdlc1doubles';
		if (format.includes('bdsp')) {
			if (format.includes('doubles')) {
				this.formatType = 'bdspdoubles';
			} else {
				this.formatType = 'bdsp';
			}
			format = format.slice(4) as ID;
			this.dex = Dex.mod('gen8bdsp' as ID);
		}
		if (format.includes('bw1')) {
			this.formatType = 'bw1';
			this.dex = Dex.mod('gen5bw1' as ID);
		}
		if (format === 'partnersincrime') this.formatType = 'doubles';
		if (format.startsWith('ffa') || format === 'freeforall') this.formatType = 'doubles';
		if (format.includes('letsgo')) {
			this.formatType = 'letsgo';
			this.dex = Dex.mod('gen7letsgo' as ID);
		}
		if (format.includes('nationaldex') || format.startsWith('nd') || format.includes('natdex')) {
			format = (format.startsWith('nd') ? format.slice(2) :
				format.includes('natdex') ? format.slice(6) : format.slice(11)) as ID;
			this.formatType = 'natdex';
			if (!format) format = 'ou' as ID;
		}
		if (format.includes('doubles') && this.dex.gen > 4 && !this.formatType) this.formatType = 'doubles';
		if (this.formatType === 'letsgo') format = format.slice(6) as ID;
		if (format.includes('metronome')) {
			this.formatType = 'metronome';
		}
		if (format.endsWith('nfe')) {
			format = format.slice(3) as ID;
			this.formatType = 'nfe';
			if (!format) format = 'ou' as ID;
		}
		if ((format.endsWith('lc') || format.startsWith('lc')) && format !== 'caplc' && !this.formatType) {
			this.formatType = 'lc';
			format = 'lc' as ID;
		}
		if (format.endsWith('draft')) format = format.slice(0, -5) as ID;
		this.format = format;

		this.species = '' as ID;
		this.set = null;
		if (typeof speciesOrSet === 'string') {
			if (speciesOrSet) this.species = speciesOrSet;
		} else {
			this.set = speciesOrSet;
			this.species = toID(this.set.species);
		}
		// if (!searchType || !this.set) return;
	}
	getResults(filters?: SearchFilter[] | null, sortCol?: string | null, reverseSort?: boolean): SearchRow[] {
		if (sortCol === 'type') {
			return [this.sortRow!, ...BattleTypeSearch.prototype.getDefaultResults.call(this)];
		} else if (sortCol === 'category') {
			return [this.sortRow!, ...BattleCategorySearch.prototype.getDefaultResults.call(this)];
		} else if (sortCol === 'ability') {
			return [this.sortRow!, ...BattleAbilitySearch.prototype.getDefaultResults.call(this)];
		}

		if (!this.baseResults) {
			this.baseResults = this.getBaseResults();
		}

		if (!this.baseIllegalResults) {
			const legalityFilter: { [id: string]: 1 } = {};
			for (const [resultType, value] of this.baseResults) {
				if (resultType === this.searchType) legalityFilter[value] = 1;
			}
			this.baseIllegalResults = [];
			this.illegalReasons = {};

			for (const id in this.getTable()) {
				if (!(id in legalityFilter)) {
					this.baseIllegalResults.push([this.searchType, id as ID]);
					this.illegalReasons[id] = 'Illegal';
				}
			}
		}

		let results: SearchRow[];
		let illegalResults: SearchRow[] | null;

		if (filters) {
			results = [];
			illegalResults = [];
			for (const result of this.baseResults) {
				if (this.filter(result, filters)) {
					if (results.length && result[0] === 'header' && results[results.length - 1][0] === 'header') {
						results[results.length - 1] = result;
					} else {
						results.push(result);
					}
				}
			}
			if (results.length && results[results.length - 1][0] === 'header') {
				results.pop();
			}
			for (const result of this.baseIllegalResults) {
				if (this.filter(result, filters)) {
					illegalResults.push(result);
				}
			}
		} else {
			results = [...this.baseResults];
			illegalResults = null;
		}
		if (this.defaultFilter) {
			results = this.defaultFilter(results);
		}

		if (sortCol) {
			results = results.filter(([rowType]) => rowType === this.searchType);
			results = this.sort(results, sortCol, reverseSort);
			if (illegalResults) {
				illegalResults = illegalResults.filter(([rowType]) => rowType === this.searchType);
				illegalResults = this.sort(illegalResults, sortCol, reverseSort);
			}
		}

		if (this.sortRow) {
			results = [this.sortRow, ...results];
		}
		if (illegalResults?.length) {
			results = [...results, ['header', "Illegal results"], ...illegalResults];
		}
		return results;
	}
	protected firstLearnsetid(speciesid: ID) {
		let table = BattleTeambuilderTable;
		if (this.formatType?.startsWith('bdsp')) table = table['gen8bdsp'];
		if (this.formatType === 'letsgo') table = table['gen7letsgo'];
		if (this.formatType === 'bw1') table = table['gen5bw1'];
		if (speciesid in table.learnsets) return speciesid;
		const species = this.dex.species.get(speciesid);
		if (!species.exists) return '' as ID;

		let baseLearnsetid = toID(species.baseSpecies);
		if (typeof species.battleOnly === 'string' && species.battleOnly !== species.baseSpecies) {
			baseLearnsetid = toID(species.battleOnly);
		}
		if (speciesid === 'darmanitangalarzen') {
			baseLearnsetid = toID("Darmanitan-Galar");
		}
		if (baseLearnsetid in table.learnsets) return baseLearnsetid;
		return '' as ID;
	}
	protected nextLearnsetid(learnsetid: ID, speciesid: ID, checkingMoves = false) {
		if (learnsetid === 'lycanrocdusk' || (speciesid === 'rockruff' && learnsetid === 'rockruff')) {
			return 'rockruffdusk' as ID;
		}
		const lsetSpecies = this.dex.species.get(learnsetid);
		if (!lsetSpecies.exists) return '' as ID;

		if (lsetSpecies.id === 'gastrodoneast') return 'gastrodon' as ID;
		if (lsetSpecies.id === 'pumpkaboosuper') return 'pumpkaboo' as ID;
		if (lsetSpecies.id === 'sinisteaantique') return 'sinistea' as ID;
		if (lsetSpecies.id === 'tatsugiristretchy') return 'tatsugiri' as ID;

		const next = lsetSpecies.battleOnly || lsetSpecies.changesFrom || lsetSpecies.prevo;
		if (next) return toID(next);

		if (checkingMoves && !lsetSpecies.prevo && lsetSpecies.baseSpecies &&
			this.dex.species.get(lsetSpecies.baseSpecies).prevo) {
			let baseEvo = this.dex.species.get(lsetSpecies.baseSpecies);
			while (baseEvo.prevo) {
				baseEvo = this.dex.species.get(baseEvo.prevo);
			}
			return toID(baseEvo);
		}

		return '' as ID;
	}
	protected canLearn(speciesid: ID, moveid: ID) {
		const move = this.dex.moves.get(moveid);
		if (this.formatType === 'natdex' && move.isNonstandard && move.isNonstandard !== 'Past') {
			return false;
		}
		const gen = this.dex.gen;
		let genChar = `${gen}`;
		if (
			this.format.startsWith('vgc') ||
			this.format.startsWith('bss') ||
			this.format.startsWith('battlespot') ||
			this.format.startsWith('battlestadium') ||
			this.format.startsWith('battlefestival') ||
			(this.dex.gen === 9 && this.formatType !== 'natdex')
		) {
			if (gen === 9) {
				genChar = 'a';
			} else if (gen === 8) {
				genChar = 'g';
			} else if (gen === 7) {
				genChar = 'q';
			} else if (gen === 6) {
				genChar = 'p';
			}
		}
		let learnsetid = this.firstLearnsetid(speciesid);
		while (learnsetid) {
			let table = BattleTeambuilderTable;
			if (this.formatType?.startsWith('bdsp')) table = table['gen8bdsp'];
			if (this.formatType === 'letsgo') table = table['gen7letsgo'];
			if (this.formatType === 'bw1') table = table['gen5bw1'];
			let learnset = table.learnsets[learnsetid];
			const eggMovesOnly = this.eggMovesOnly(learnsetid, speciesid);
			if (learnset && (moveid in learnset) && (!this.format.startsWith('tradebacks') ? learnset[moveid].includes(genChar) :
				learnset[moveid].includes(genChar) || (learnset[moveid].includes(`${gen + 1}`) && move.gen === gen)) &&
				(!eggMovesOnly || (learnset[moveid].includes('e') && this.dex.gen === 9))
			) {
				return true;
			}
			learnsetid = this.nextLearnsetid(learnsetid, speciesid, true);
		}
		return false;
	}
	getTier(pokemon: Dex.Species) {
		if (this.formatType === 'metronome') {
			return pokemon.num >= 0 ? String(pokemon.num) : pokemon.tier;
		}
		let table = window.BattleTeambuilderTable;
		const gen = this.dex.gen;
		const tableKey = this.formatType === 'doubles' ? `gen${gen}doubles` :
			this.formatType === 'letsgo' ? 'gen7letsgo' :
			this.formatType === 'bdsp' ? 'gen8bdsp' :
			this.formatType === 'bdspdoubles' ? 'gen8bdspdoubles' :
			this.formatType === 'bw1' ? 'gen5bw1' :
			this.formatType === 'nfe' ? `gen${gen}nfe` :
			this.formatType === 'lc' ? `gen${gen}lc` :
			this.formatType === 'ssdlc1' ? 'gen8dlc1' :
			this.formatType === 'ssdlc1doubles' ? 'gen8dlc1doubles' :
			this.formatType === 'predlc' ? 'gen9predlc' :
			this.formatType === 'predlcdoubles' ? 'gen9predlcdoubles' :
			this.formatType === 'predlcnatdex' ? 'gen9predlcnatdex' :
			this.formatType === 'svdlc1' ? 'gen9dlc1' :
			this.formatType === 'svdlc1doubles' ? 'gen9dlc1doubles' :
			this.formatType === 'svdlc1natdex' ? 'gen9dlc1natdex' :
			this.formatType === 'natdex' ? `gen${gen}natdex` :
			this.formatType === 'stadium' ? `gen${gen}stadium${gen > 1 ? gen : ''}` :
			`gen${gen}`;
		if (table?.[tableKey]) {
			table = table[tableKey];
		}
		if (!table) return pokemon.tier;

		let id = pokemon.id;
		if (id in table.overrideTier) {
			return table.overrideTier[id];
		}
		if (id.endsWith('totem') && id.slice(0, -5) in table.overrideTier) {
			return table.overrideTier[id.slice(0, -5)];
		}
		id = toID(pokemon.baseSpecies);
		if (id in table.overrideTier) {
			return table.overrideTier[id];
		}

		return pokemon.tier;
	}
	eggMovesOnly(child: ID, father: ID) {
		if (this.dex.species.get(child).baseSpecies === this.dex.species.get(father).baseSpecies) return false;
		const baseSpecies = father;
		while (father) {
			if (child === father) return false;
			father = this.nextLearnsetid(father, baseSpecies);
		}
		return true;
	}
	abstract getTable(): { [id: string]: any };
	abstract getDefaultResults(): SearchRow[];
	abstract getBaseResults(): SearchRow[];
	abstract filter(input: SearchRow, filters: string[][]): boolean;
	defaultFilter?(input: SearchRow[]): SearchRow[];
	abstract sort(input: SearchRow[], sortCol: string, reverseSort?: boolean): SearchRow[];
}

class BattlePokemonSearch extends BattleTypedSearch<'pokemon'> {
	override sortRow: SearchRow = ['sortpokemon', ''];
	getTable() {
		return BattlePokedex;
	}
	getDefaultResults(): SearchRow[] {
		let results: SearchRow[] = [];
		for (let id in BattlePokedex) {
			switch (id) {
			case 'bulbasaur':
				results.push(['header', "Generation 1"]);
				break;
			case 'chikorita':
				results.push(['header', "Generation 2"]);
				break;
			case 'treecko':
				results.push(['header', "Generation 3"]);
				break;
			case 'turtwig':
				results.push(['header', "Generation 4"]);
				break;
			case 'victini':
				results.push(['header', "Generation 5"]);
				break;
			case 'chespin':
				results.push(['header', "Generation 6"]);
				break;
			case 'rowlet':
				results.push(['header', "Generation 7"]);
				break;
			case 'grookey':
				results.push(['header', "Generation 8"]);
				break;
			case 'sprigatito':
				results.push(['header', "Generation 9"]);
				break;
			case 'missingno':
				results.push(['header', "Glitch"]);
				break;
			case 'syclar':
				results.push(['header', "CAP"]);
				break;
			case 'pikachucosplay':
				continue;
			}
			results.push(['pokemon', id as ID]);
		}
		return results;
	}
	getBaseResults(): SearchRow[] {
		const format = this.format;
		if (!format) return this.getDefaultResults();
		const isVGCOrBS = format.startsWith('battlespot') || format.startsWith('bss') ||
			format.startsWith('battlestadium') || format.startsWith('vgc');
		const isHackmons = format.includes('hackmons') || format.endsWith('bh');
		let isDoublesOrBS = isVGCOrBS || this.formatType?.includes('doubles');
		const dex = this.dex;

		let table = BattleTeambuilderTable;
		if ((format.endsWith('cap') || format.endsWith('caplc')) && dex.gen < 9) {
			table = table[`gen${dex.gen}`];
		} else if (isVGCOrBS) {
			table = table[`gen${dex.gen}vgc`];
		} else if (dex.gen === 9 && isHackmons && !this.formatType) {
			table = table['bh'];
		} else if (
			table[`gen${dex.gen}doubles`] && dex.gen > 4 &&
			this.formatType !== 'letsgo' && this.formatType !== 'bdspdoubles' &&
			this.formatType !== 'ssdlc1doubles' && this.formatType !== 'predlcdoubles' &&
			this.formatType !== 'svdlc1doubles' && !this.formatType?.includes('natdex') &&
			(
				format.includes('doubles') || format.includes('triples') ||
				format === 'freeforall' || format.startsWith('ffa') ||
				format === 'partnersincrime'
			)
		) {
			table = table[`gen${dex.gen}doubles`];
			isDoublesOrBS = true;
		} else if (dex.gen < 9 && !this.formatType) {
			table = table[`gen${dex.gen}`];
		} else if (this.formatType?.startsWith('bdsp')) {
			table = table['gen8' + this.formatType];
		} else if (this.formatType === 'letsgo') {
			table = table['gen7letsgo'];
		} else if (this.formatType === 'bw1') {
			table = table['gen5bw1'];
		} else if (this.formatType === 'natdex') {
			table = table[`gen${dex.gen}natdex`];
		} else if (this.formatType === 'metronome') {
			table = table[`gen${dex.gen}metronome`];
		} else if (this.formatType === 'nfe') {
			table = table[`gen${dex.gen}nfe`];
		} else if (this.formatType === 'lc') {
			table = table[`gen${dex.gen}lc`];
		} else if (this.formatType?.startsWith('ssdlc1')) {
			if (this.formatType.includes('doubles')) {
				table = table['gen8dlc1doubles'];
			} else {
				table = table['gen8dlc1'];
			}
		} else if (this.formatType?.startsWith('predlc')) {
			if (this.formatType.includes('doubles')) {
				table = table['gen9predlcdoubles'];
			} else if (this.formatType.includes('natdex')) {
				table = table['gen9predlcnatdex'];
			} else {
				table = table['gen9predlc'];
			}
		} else if (this.formatType?.startsWith('svdlc1')) {
			if (this.formatType.includes('doubles')) {
				table = table['gen9dlc1doubles'];
			} else if (this.formatType.includes('natdex')) {
				table = table['gen9dlc1natdex'];
			} else {
				table = table['gen9dlc1'];
			}
		} else if (this.formatType === 'stadium') {
			table = table[`gen${dex.gen}stadium${dex.gen > 1 ? dex.gen : ''}`];
		}

		if (!table.tierSet) {
			table.tierSet = table.tiers.map((r: any) => {
				if (typeof r === 'string') return ['pokemon', r];
				return [r[0], r[1]];
			});
			table.tiers = null;
		}
		let tierSet: SearchRow[] = table.tierSet;
		let slices: { [k: string]: number } = table.formatSlices;
		if (format === 'ubers' || format === 'uber' || format === 'ubersuu' || format === 'nationaldexdoubles') {
			tierSet = tierSet.slice(slices.Uber);
		} else if (isVGCOrBS || (isHackmons && dex.gen === 9 && !this.formatType)) {
			if (format.endsWith('series13') || isHackmons) {
				// Show Mythicals
			} else if (
				format === 'vgc2010' || format === 'vgc2016' || format.startsWith('vgc2019') ||
				format === 'vgc2022' || format.endsWith('regg') || format.endsWith('regi')
			) {
				tierSet = tierSet.slice(slices["Restricted Legendary"]);
			} else {
				tierSet = tierSet.slice(slices.Regular);
			}

			if (format.endsWith('regh')) {
				tierSet = tierSet.filter(([type, id]) => {
					const tags = Dex.species.get(Dex.species.get(id).baseSpecies).tags;
					return !tags.includes('Sub-Legendary') && !tags.includes('Paradox') &&
						// The game does not classify these as Paradox Pokemon (Booster Energy can be knocked off)
						!['gougingfire', 'ironboulder', 'ironcrown', 'ragingbolt'].includes(id);
				});
			}
		} else if (format === 'ou') tierSet = tierSet.slice(slices.OU);
		else if (format === 'uubl') tierSet = tierSet.slice(slices.UUBL);
		else if (format === 'uu') tierSet = tierSet.slice(slices.UU);
		else if (format === 'ru') tierSet = tierSet.slice(slices.RU || slices.UU);
		else if (format === 'nu') tierSet = tierSet.slice(slices.NU || slices.RU || slices.UU);
		else if (format === 'pu') tierSet = tierSet.slice(slices.PU || slices.NU);
		else if (format === 'zu' && dex.gen === 5) tierSet = tierSet.slice(slices.PU || slices.NU);
		else if (format === 'zu') tierSet = tierSet.slice(slices.ZU || slices.PU || slices.NU);
		else if (
			format === 'lc' || format === 'lcuu' || format.startsWith('lc') || (format !== 'caplc' && format.endsWith('lc'))
		) tierSet = tierSet.slice(slices.LC);
		else if (format === 'cap' || format.endsWith('cap')) {
			tierSet = tierSet.slice(0, slices.AG || slices.Uber).concat(tierSet.slice(slices.OU));
		} else if (format === 'caplc') {
			tierSet = tierSet.slice(slices['CAP LC'], slices.AG || slices.Uber).concat(tierSet.slice(slices.LC));
		} else if (format === 'anythinggoes' || format.endsWith('ag') || format.startsWith('ag')) {
			tierSet = tierSet.slice(slices.AG);
		} else if (isHackmons && (dex.gen < 9 || this.formatType === 'natdex')) {
			tierSet = tierSet.slice(slices.AG || slices.Uber);
		} else if (format === 'monotype' || format.startsWith('monothreat')) tierSet = tierSet.slice(slices.Uber);
		else if (format === 'doublesubers') tierSet = tierSet.slice(slices.DUber);
		else if (format === 'doublesou' && dex.gen > 4) tierSet = tierSet.slice(slices.DOU);
		else if (format === 'doublesuu') tierSet = tierSet.slice(slices.DUU);
		else if (format === 'doublesnu') tierSet = tierSet.slice(slices.DNU || slices.DUU);
		else if (this.formatType?.startsWith('bdsp') || this.formatType === 'letsgo' || this.formatType === 'stadium') {
			tierSet = tierSet.slice(slices.Uber);
		} else if (!isDoublesOrBS) {
			tierSet = [
				...tierSet.slice(slices.OU, slices.UU),
				...tierSet.slice(slices.AG, slices.Uber),
				...tierSet.slice(slices.Uber, slices.OU),
				...tierSet.slice(slices.UU),
			];
		} else {
			tierSet = [
				...tierSet.slice(slices.DOU, slices.DUU),
				...tierSet.slice(slices.DUber, slices.DOU),
				...tierSet.slice(slices.DUU),
			];
		}
		if (format === 'ubersuu' && table.ubersUUBans) {
			tierSet = tierSet.filter(([type, id]) => {
				if (id in table.ubersUUBans) return false;
				return true;
			});
		}
		if (format === 'doubles' && this.formatType === 'natdex' && table.ndDoublesBans) {
			tierSet = tierSet.filter(([type, id]) => {
				if (id in table.ndDoublesBans) return false;
				return true;
			});
		}
		if (format === '35pokes' && table.thirtyfivePokes) {
			tierSet = tierSet.filter(([type, id]) => {
				if (id in table.thirtyfivePokes) return true;
				return false;
			});
		}
		if (dex.gen >= 5) {
			if ((format === 'monotype' || format.startsWith('monothreat')) && table.monotypeBans) {
				tierSet = tierSet.filter(([type, id]) => {
					if (id in table.monotypeBans) return false;
					return true;
				});
			}
		}
		if (format === 'zu' && dex.gen === 5 && table.gen5zuBans) {
			tierSet = tierSet.filter(([type, id]) => {
				if (id in table.gen5zuBans) return false;
				return true;
			});
		}

		// Filter out Gmax Pokemon from standard tier selection
		if (!/^(battlestadium|vgc|doublesubers)/g.test(format)) {
			tierSet = tierSet.filter(([type, id]) => {
				if (type === 'header' && id === 'DUber by technicality') return false;
				if (type === 'pokemon') return !id.endsWith('gmax');
				return true;
			});
		}

		return tierSet;
	}
	filter(row: SearchRow, filters: string[][]) {
		if (!filters) return true;
		if (row[0] !== 'pokemon') return true;
		const species = this.dex.species.get(row[1]);
		for (const [filterType, value] of filters) {
			switch (filterType) {
			case 'type':
				if (species.types[0] !== value && species.types[1] !== value) return false;
				break;
			case 'egggroup':
				if (species.eggGroups[0] !== value && species.eggGroups[1] !== value) return false;
				break;
			case 'tier':
				if (this.getTier(species) !== value) return false;
				break;
			case 'ability':
				if (!Dex.hasAbility(species, value)) return false;
				break;
			case 'move':
				if (!this.canLearn(species.id, value as ID)) return false;
			}
		}
		return true;
	}
	sort(results: SearchRow[], sortCol: string, reverseSort?: boolean) {
		const sortOrder = reverseSort ? -1 : 1;
		if (['hp', 'atk', 'def', 'spa', 'spd', 'spe'].includes(sortCol)) {
			return results.sort(([rowType1, id1], [rowType2, id2]) => {
				const stat1 = this.dex.species.get(id1).baseStats[sortCol as Dex.StatName];
				const stat2 = this.dex.species.get(id2).baseStats[sortCol as Dex.StatName];
				return (stat2 - stat1) * sortOrder;
			});
		} else if (sortCol === 'bst') {
			return results.sort(([rowType1, id1], [rowType2, id2]) => {
				const base1 = this.dex.species.get(id1).baseStats;
				const base2 = this.dex.species.get(id2).baseStats;
				let bst1 = base1.hp + base1.atk + base1.def + base1.spa + base1.spd + base1.spe;
				let bst2 = base2.hp + base2.atk + base2.def + base2.spa + base2.spd + base2.spe;
				if (this.dex.gen === 1) {
					bst1 -= base1.spd;
					bst2 -= base2.spd;
				}
				return (bst2 - bst1) * sortOrder;
			});
		} else if (sortCol === 'name') {
			return results.sort(([rowType1, id1], [rowType2, id2]) => {
				const name1 = id1;
				const name2 = id2;
				return (name1 < name2 ? -1 : name1 > name2 ? 1 : 0) * sortOrder;
			});
		}
		throw new Error("invalid sortcol");
	}
}

class BattleAbilitySearch extends BattleTypedSearch<'ability'> {
	getTable() {
		return BattleAbilities;
	}
	getDefaultResults(): SearchRow[] {
		const results: SearchRow[] = [];
		for (let id in BattleAbilities) {
			results.push(['ability', id as ID]);
		}
		return results;
	}
	getBaseResults() {
		if (!this.species) return this.getDefaultResults();
		const format = this.format;
		const isHackmons = (format.includes('hackmons') || format.endsWith('bh'));
		const isAAA = (format === 'almostanyability' || format.includes('aaa'));
		const dex = this.dex;
		let species = dex.species.get(this.species);
		let abilitySet: SearchRow[] = [['header', "Abilities"]];

		if (species.isMega) {
			abilitySet.unshift(['html', `Will be <strong>${species.abilities['0']}</strong> after Mega Evolving.`]);
			species = dex.species.get(species.baseSpecies);
		}
		abilitySet.push(['ability', toID(species.abilities['0'])]);
		if (species.abilities['1']) {
			abilitySet.push(['ability', toID(species.abilities['1'])]);
		}
		if (species.abilities['H']) {
			abilitySet.push(['header', "Hidden Ability"]);
			abilitySet.push(['ability', toID(species.abilities['H'])]);
		}
		if (species.abilities['S']) {
			abilitySet.push(['header', "Special Event Ability"]);
			abilitySet.push(['ability', toID(species.abilities['S'])]);
		}
		if (isAAA || format.includes('metronomebattle') || isHackmons) {
			let abilities: ID[] = [];
			for (let i in this.getTable()) {
				const ability = dex.abilities.get(i);
				if (ability.isNonstandard) continue;
				if (ability.gen > dex.gen) continue;
				abilities.push(ability.id);
			}

			let goodAbilities: SearchRow[] = [['header', "Abilities"]];
			let poorAbilities: SearchRow[] = [['header', "Situational Abilities"]];
			let badAbilities: SearchRow[] = [['header', "Unviable Abilities"]];
			for (const ability of abilities.sort().map(abil => dex.abilities.get(abil))) {
				let rating = ability.rating;
				if (ability.id === 'normalize') rating = 3;
				if (rating >= 3) {
					goodAbilities.push(['ability', ability.id]);
				} else if (rating >= 2) {
					poorAbilities.push(['ability', ability.id]);
				} else {
					badAbilities.push(['ability', ability.id]);
				}
			}
			abilitySet = [...goodAbilities, ...poorAbilities, ...badAbilities];
			if (species.isMega) {
				if (isAAA) {
					abilitySet.unshift(['html', `Will be <strong>${species.abilities['0']}</strong> after Mega Evolving.`]);
				}
				// species is unused after this, so no need to replace
			}
		}
		return abilitySet;
	}
	filter(row: SearchRow, filters: string[][]) {
		if (!filters) return true;
		if (row[0] !== 'ability') return true;
		const ability = this.dex.abilities.get(row[1]);
		for (const [filterType, value] of filters) {
			switch (filterType) {
			case 'pokemon':
				if (!Dex.hasAbility(this.dex.species.get(value), ability.name)) return false;
				break;
			}
		}
		return true;
	}
	sort(results: SearchRow[], sortCol: string | null, reverseSort?: boolean): SearchRow[] {
		throw new Error("invalid sortcol");
	}
}

class BattleItemSearch extends BattleTypedSearch<'item'> {
	getTable() {
		return BattleItems;
	}
	getDefaultResults(): SearchRow[] {
		let table = BattleTeambuilderTable;
		if (this.formatType?.startsWith('bdsp')) {
			table = table['gen8bdsp'];
		} else if (this.formatType === 'bw1') {
			table = table['gen5bw1'];
		} else if (this.formatType === 'natdex') {
			table = table[`gen${this.dex.gen}natdex`];
		} else if (this.formatType?.endsWith('doubles')) { // no natdex/bdsp doubles support
			table = table[`gen${this.dex.gen}doubles`];
		} else if (this.formatType === 'metronome') {
			table = table[`gen${this.dex.gen}metronome`];
		} else if (this.dex.gen < 9) {
			table = table[`gen${this.dex.gen}`];
		}
		if (!table.itemSet) {
			table.itemSet = table.items.map((r: any) => {
				if (typeof r === 'string') {
					return ['item', r];
				}
				return [r[0], r[1]];
			});
			table.items = null;
		}
		return table.itemSet;
	}
	getBaseResults(): SearchRow[] {
		if (!this.species) return this.getDefaultResults();
		const speciesName = this.dex.species.get(this.species).name;
		const results = this.getDefaultResults();
		const speciesSpecific: SearchRow[] = [];
		const abilitySpecific: SearchRow[] = [];
		const abilityItem = {
			protosynthesis: 'boosterenergy',
			quarkdrive: 'boosterenergy',
			// poisonheal: 'toxicorb',
			// toxicboost: 'toxicorb',
			// flareboost: 'flameorb',
		}[toID(this.set?.ability) as string];
		for (const row of results) {
			if (row[0] !== 'item') continue;
			const item = this.dex.items.get(row[1]);
			if (item.itemUser?.includes(speciesName)) speciesSpecific.push(row);
			if (abilityItem === item.id) abilitySpecific.push(row);
		}
		if (speciesSpecific.length) {
			return [
				['header', "Specific to " + speciesName],
				...speciesSpecific,
				...results,
			];
		}
		if (abilitySpecific.length) {
			return [
				['header', `Specific to ${this.set!.ability!}`],
				...abilitySpecific,
				...results,
			];
		}
		return results;
	}
	override defaultFilter(results: SearchRow[]) {
		if (this.species && !this.dex.species.get(this.species).nfe) {
			results.splice(results.findIndex(row => row[1] === 'eviolite'), 1);
			return results;
		}
		return results;
	}
	filter(row: SearchRow, filters: string[][]) {
		return true;
	}
	sort(results: SearchRow[], sortCol: string | null, reverseSort?: boolean): SearchRow[] {
		throw new Error("invalid sortcol");
	}
}

class BattleMoveSearch extends BattleTypedSearch<'move'> {
	override sortRow: SearchRow = ['sortmove', ''];
	getTable() {
		return BattleMovedex;
	}
	getDefaultResults(): SearchRow[] {
		let results: SearchRow[] = [];
		results.push(['header', "Moves"]);
		for (let id in BattleMovedex) {
			switch (id) {
			case 'paleowave':
				results.push(['header', "CAP moves"]);
				break;
			case 'magikarpsrevenge':
				continue;
			}
			results.push(['move', id as ID]);
		}
		return results;
	}
	private moveIsNew(id: ID, species: Dex.Species) {
		switch (species.name) {
		case 'Ivysaur':
		case 'Venusaur':
		case 'Venusaur-Mega':
			if (['ancientpower', 'rockslide', 'dazzlinggleam', 'mysticalfire', 'spore'].includes(id)) { return true; }
			break;
		case 'Charizard':
		case 'Charizard-Mega-X':
		case 'Charizard-Mega-Y':
			if (['dracometeor', 'bravebird', 'aeroblast'].includes(id)) { return true; }
			break;
		case 'Wartortle':
		case 'Blastoise':
		case 'Blastoise-Mega':
			if (['sludgebomb', 'energyball'].includes(id)) { return true; }
			break;
		case 'Butterfree':
			if (['synchronoise', 'focusblast', 'bleakwindstorm', 'blizzard', 'zapcannon'].includes(id)) { return true; }
			break;
		case 'Beedrill':
		case 'Beedrill-Mega':
			if (['drillpeck', 'superpower'].includes(id)) { return true; }
			break;
		case 'Pidgeot':
		case 'Pidgeot-Mega':
			if (['zapcannon', 'fireblast', 'focusblast'].includes(id)) { return true; }
			break;
		case 'Rattata-Mega':
		case 'Raticate':
			if (['playrough', 'stuffcheeks', 'lastresort', 'superpower', 'submission'].includes(id)) { return true; }
			break;
		case 'Raticate-Alola':
			if (['playrough', 'stuffcheeks', 'lastresort', 'superpower', 'submission', 'partingshot', 'jawlock'].includes(id)) { return true; }
			break;
		case 'Fearow':
			if (['crunch', 'suckerpunch', 'wickedblow'].includes(id)) { return true; }
			break;
		case 'Arbok':
			if (['crosspoison', 'recover', 'mortalspin', 'jawlock', 'partingshot', 'hypnosis', 'dragondance', 'shedtail'].includes(id)) { return true; }
			break;
		case 'Pikachu':
		case 'Pikachu-Cosplay':
		case 'Pikachu-Rock-Star':
		case 'Pikachu-Belle':
		case 'Pikachu-Pop-Star':
		case 'Pikachu-PhD':
		case 'Pikachu-Libre':
		case 'Pikachu-Original':
		case 'Pikachu-Hoenn':
		case 'Pikachu-Sinnoh':
		case 'Pikachu-Unova':
		case 'Pikachu-Kalos':
		case 'Pikachu-Alola':
		case 'Pikachu-Partner':
		case 'Pikachu-Starter':
		case 'Pikachu-Gmax':
		case 'Pikachu-World':
		case 'Raichu':
		case 'Raichu-Mega-X':
		case 'Raichu-Mega-Y':
			if (['paraboliccharge'].includes(id)) { return true; }
			break;
		case 'Raichu-Alola':
			if (['paraboliccharge'].includes(id)) { return true; }
			break;
		case 'Sandslash':
			if (['boneclub', 'crosspoison', 'spikyshield', 'headlongrush', 'firstimpression'].includes(id)) { return true; }
			break;
		case 'Sandslash-Alola':
			if (['icespinner', 'spikyshield', 'firstimpression'].includes(id)) { return true; }
			break;
		case 'Nidoking':
			if (['crunch', 'rockclimb', 'poisontail'].includes(id)) { return true; }
			break;
		case 'Nidoqueen':
			if (['crunch', 'followme', 'poisontail'].includes(id)) { return true; }
			break;
		case 'Clefairy':
		case 'Clefable':
		case 'Clefable-Mega':
			if (['doomdesire', 'springtidestorm', 'lightofruin'].includes(id)) { return true; }
			break;
		case 'Vulpix':
		case 'Ninetales':
			if (['solarblade', 'futuresight', 'burnup', 'psychicterrain'].includes(id)) { return true; }
			break;
		case 'Vulpix-Alola':
		case 'Ninetales-Alola':
			if (['iceburn', 'freezeshock', 'metalsound', 'glaciate', 'springtidestorm', 'freezyfrost'].includes(id)) { return true; }
			break;
		case 'Wigglytuff':
			if (['moonblast', 'softboiled', 'boomburst'].includes(id)) { return true; }
			break;
		case 'Crobat':
			if (['poisongas', 'gunkshot', 'glare', 'bleakwindstorm', 'honeclaws', 'knockoff'].includes(id)) { return true; }
			break;
		case 'Gloom':
		case 'Vileplume':
			if (['knockoff', 'flowertrick', 'junglehealing', 'spikyshield', 'seedflare'].includes(id)) { return true; }
			break;
		case 'Bellossom':
			if (['teeterdance', 'overheat', 'revelationdance', 'fierydance'].includes(id)) { return true; }
			break;
		case 'Parasect':
			if (['shadowsneak', 'nightmare', 'shadowclaw', 'destinybond', 'willowisp', 'leafblade', 'poltergeist', 'twineedle', 'solarblade', 'healorder', 'uturn', 'crosspoison', 'ceaselessedge', 'stickyweb'].includes(id)) { return true; }
			break;
		case 'Venomoth':
			if (['shadowball', 'hurricane'].includes(id)) { return true; }
			break;
		case 'Diglett':
		case 'Dugtrio':
			if (['boneclub', 'superfang', 'rockclimb', 'shoreup', 'fissure', 'headslongrush', 'tripleaxel', 'magnetrise', 'closecombat'].includes(id)) { return true; }
			break;
		case 'Dugtrio-Alola':
			if (['boneclub', 'fissure', 'headslongrush', 'tripleaxel', 'magnetrise', 'closecombat'].includes(id)) { return true; }
			break;
		case 'Wugtrio':
			if (['glare', 'watershuriken', 'ironhead', 'superfang', 'fakeout', 'flipturn', 'tripleaxel', 'jetpunch'].includes(id)) { return true; }
			break;
		case 'Golduck':
			if (['aquacutter', 'dazzlinggleam', 'psychicfangs', 'photongeyser', 'recover', 'followme'].includes(id)) { return true; }
			break;
		case 'Persian':
			if (['return', 'doublehit', 'partingshot', 'suckerpunch', 'swordsdance'].includes(id)) { return true; }
			break;
		case 'Persian-Alola':
			if (['payday', 'hyperspacefury', 'nightmare', 'cottonguard', 'jawlock', 'darkvoid'].includes(id)) { return true; }
			break;
		case 'Perrserker':
			if (['steelroller', 'suckerpunch', 'bulletpunch', 'meteormash', 'makeitrain', 'nastyplot', 'shiftgear'].includes(id)) { return true; }
			break;
		case 'Primeape':
		case 'Annihilape':
			if (['machpunch', 'willowisp'].includes(id)) { return true; }
			break;
		case 'Poliwrath':
			if (['perishsong', 'wavecrash', 'flipturn', 'jetpunch'].includes(id)) { return true; }
			break;
		case 'Politoed':
			if (['muddywater', 'flipturn', 'bouncybubble', 'lovelykiss', 'nastyplot'].includes(id)) { return true; }
			break;
		case 'Kadabra':
		case 'Alakazam':
		case 'Alakazam-Mega':
			if (['aurasphere'].includes(id)) { return true; }
			break;
		case 'Machoke':
		case 'Machamp':
			if (['fissure'].includes(id)) { return true; }
			break;
		case 'Haunter':
		case 'Gengar':
		case 'Gengar-Mega':
			if (['aurasphere'].includes(id)) { return true; }
			break;
		case 'Tentacool':
		case 'Tentacruel':
			if (['muddywater', 'corrosivegas', 'lifedew', 'mortalspin', 'octazooka'].includes(id)) { return true; }
			break;
		case 'Graveler':
		case 'Golem':
			if (['rapidspin', 'headsmash', 'shoreup'].includes(id)) { return true; }
			break;
		case 'Graveler-Alola':
		case 'Golem-Alola':
			if (['rapidspin', 'zingzap', 'magnetrise', 'icepunch', 'headsmash', 'boltstrike'].includes(id)) { return true; }
			break;
		case 'Rapidash':
			if (['headcharge', 'ragingfury', 'uturn'].includes(id)) { return true; }
			break;
		case 'Rapidash-Galar':
			if (['psyshieldbash', 'headcharge', 'wish', 'uturn'].includes(id)) { return true; }
			break;
		case 'Slowbro':
		case 'Slowbro-Mega':
			if (['mirrorcoat', 'wideguard', 'mysticalpower'].includes(id)) { return true; }
			break;
		case 'Slowking':
			if (['mirrorcoat', 'kingsshield', 'mysticalpower'].includes(id)) { return true; }
			break;
		case 'Slowbro-Galar':
			if (['teleport', 'mirrorcoat', 'banefulbunker'].includes(id)) { return true; }
			break;
		case 'Slowking-Galar':
			if (['mirrorcoat'].includes(id)) { return true; }
			break;
		case 'Magneton':
		case 'Magnezone':
			if (['mysticalfire'].includes(id)) { return true; }
			break;
		case 'Farfetch\u2019d':
			if (['bitterblade', 'floatyfall', 'glaciallance'].includes(id)) { return true; }
			break;
		case 'Farfetch\u2019d-Galar':
			if (['bitterblade', 'floatyfall', 'glaciallance'].includes(id)) { return true; }
			break;
		case 'Sirfetch\u2019d':
			if (['machpunch', 'drillpeck', 'roost', 'uturn', 'bitterblade', 'glaciallance'].includes(id)) { return true; }
			break;
		case 'Dodrio':
			if (['closecombat', 'wildcharge', 'extremespeed'].includes(id)) { return true; }
			break;
		case 'Dewgong':
			if (['iceburn', 'freezeshock', 'freezedry', 'wavecrash', 'snipeshot', 'flipturn'].includes(id)) { return true; }
			break;
		case 'Muk':
			if (['psychicfangs', 'crosspoison', 'suckerpunch'].includes(id)) { return true; }
			break;
		case 'Muk-Alola':
			if (['crosspoison', 'suckerpunch', 'partingshot', 'toxicspikes'].includes(id)) { return true; }
			break;
		case 'Cloyster':
			if (['freezeshock', 'watershuriken'].includes(id)) { return true; }
			break;
		case 'Hypno':
			if (['superpower', 'submission', 'aurasphere', 'teleport', 'closecombat', 'bulkup', 'lovelykiss'].includes(id)) { return true; }
			break;
		case 'Kingler':
			if (['bulletpunch', 'magnetbomb', 'powerwhip', 'icehammer', 'meteormash'].includes(id)) { return true; }
			break;
		case 'Electrode':
			if (['hypervoice', 'flashcannon', 'rapidspin', 'mistyexplosion', 'risingvoltage', 'nastyplot'].includes(id)) { return true; }
			break;
		case 'Electrode-Hisui':
			if (['rapidspin', 'mistyexplosion', 'nastyplot', 'risingvoltage'].includes(id)) { return true; }
			break;
		case 'Exeggutor':
			if (['dragonbreath', 'twinbeam', 'softboiled', 'sappyseed'].includes(id)) { return true; }
			break;
		case 'Exeggutor-Alola':
			if (['softboiled', 'eggbomb', 'orderup', 'barrage', 'dragondance'].includes(id)) { return true; }
			break;
		case 'Marowak':
			if (['icepunch', 'shadowbone', 'spikes', 'perishsong', 'closecombat'].includes(id)) { return true; }
			break;
		case 'Marowak-Alola':
			if (['icepunch', 'moonlight', 'spikes', 'shadowsneak', 'perishsong', 'spectralthief'].includes(id)) { return true; }
			break;
		case 'Steelix':
		case 'Steelix-Mega':
			if (['fissure', 'metalburst', 'headlongrush', 'coil'].includes(id)) { return true; }
			break;
		case 'Hitmonlee':
			if (['tropkick', 'submission', 'tripleaxel', 'thunderouskick', 'aquastep', 'followme'].includes(id)) { return true; }
			break;
		case 'Hitmonchan':
			if (['suckerpunch', 'jetpunch', 'meteormash', 'ragefist', 'followme'].includes(id)) { return true; }
			break;
		case 'Hitmontop':
			if (['spinout', 'rocktomb', 'covet', 'topsyturvy', 'playrough', 'followme'].includes(id)) { return true; }
			break;
		case 'Lickilicky':
			if (['boomburst', 'milkdrink', 'stockpile', 'spitup', 'swallow', 'irondefense', 'bellydrum'].includes(id)) { return true; }
			break;
		case 'Weezing':
			if (['twinbeam', 'recover', 'banefulbunker', 'toxicspikes'].includes(id)) { return true; }
			break;
		case 'Weezing-Galar':
			if (['moonblast', 'recover', 'toxicspikes'].includes(id)) { return true; }
			break;
		case 'Rhyperior':
			if (['headsmash'].includes(id)) { return true; }
			break;
		case 'Tangrowth':
			if (['woodhammer', 'needlearm', 'weatherball'].includes(id)) { return true; }
			break;
		case 'Kangaskhan':
		case 'Kangaskhan-Mega':
			if (['closecombat', 'trailblaze', 'populationbomb'].includes(id)) { return true; }
			break;
		case 'Kingdra':
			if (['sludgebomb', 'gunkshot', 'flipturn', 'highhorsepower'].includes(id)) { return true; }
			break;
		case 'Seaking':
			if (['nightslash', 'lifedew', 'wavecrash', 'waterspout', 'fishiousrend', 'hydrocannon', 'dragondance'].includes(id)) { return true; }
			break;
		case 'Starmie':
		case 'Starmie-Mega':
			if (['aurasphere', 'calmmind', 'bulkup'].includes(id)) { return true; }
			break;
		case 'Mr. Mime':
			if (['extrasensory', 'spiritbreak', 'moonblast'].includes(id)) { return true; }
			break;
		case 'Mr. Rime':
			if (['freezeshock', 'iceburn', 'uturn', 'quiverdance'].includes(id)) { return true; }
			break;
		case 'Pinsir':
		case 'Pinsir-Mega':
			if (['fakeout', 'spikes', 'extremespeed'].includes(id)) { return true; }
			break;
		case 'Scizor':
		case 'Scizor-Mega':
			if (['metalburst'].includes(id)) { return true; }
			break;
		case 'Kleavor':
			if (['accelerock', 'wideguard'].includes(id)) { return true; }
			break;
		case 'Jynx':
			if (['wish', 'mysticalfire', 'freezedry'].includes(id)) { return true; }
			break;
		case 'Electabuzz':
		case 'Electivire':
			if (['machpunch', 'aurasphere', 'drainpunch', 'superpower', 'closecombat', 'plasmafists', 'bulletpunch'].includes(id)) { return true; }
			break;
		case 'Magmar':
		case 'Magmortar':
			if (['hypervoice', 'drainpunch', 'sludgebomb', 'armorcannon', 'calmmind'].includes(id)) { return true; }
			break;
		case 'Tauros':
			if (['suckerpunch', 'swordsdance'].includes(id)) { return true; }
			break;
		case 'Tauros-Paldea-Blaze':
			if (['incinerate', 'solarbeam'].includes(id)) { return true; }
			break;
		case 'Tauros-Paldea-Aqua':
			if (['scald', 'flipturn'].includes(id)) { return true; }
			break;
		case 'Gyarados':
		case 'Gyarados-Mega':
			if (['flipturn', 'dragonascent'].includes(id)) { return true; }
			break;
		case 'Lapras':
			if (['freezeshock', 'icespinner', 'flipturn', 'recover'].includes(id)) { return true; }
			break;
		case 'Eevee':
		case 'Eevee-Starter':
			if (['grassknot', 'moonlight', 'payday', 'extremespeed', 'boomburst'].includes(id)) { return true; }
			break;
		case 'Vaporeon':
			if (['grassknot', 'moonlight', 'payday', 'extremespeed', 'boomburst', 'watergun', 'clamp', 'aquajet', 'lifedew', 'watershuriken', 'bouncybubble', 'flipturn', 'muddywater', 'steameruption', 'nastyplot'].includes(id)) { return true; }
			break;
		case 'Jolteon':
			if (['grassknot', 'moonlight', 'payday', 'extremespeed', 'boomburst', 'spark', 'paraboliccharge', 'nuzzle', 'electroball', 'buzzybuzz', 'thundercage'].includes(id)) { return true; }
			break;
		case 'Flareon':
			if (['grassknot', 'moonlight', 'payday', 'extremespeed', 'boomburst', 'flamewheel', 'flameburst', 'burningbulwark', 'blueflare', 'sizzlyslide', 'teleport', 'sacredfire', 'swordsdance'].includes(id)) { return true; }
			break;
		case 'Espeon':
			if (['grassknot', 'moonlight', 'payday', 'extremespeed', 'boomburst', 'psychocut', 'hypnosis', 'cosmicpower', 'mysticalpower', 'glitzyglow', 'teleport', 'lusterpurge', 'expandingforce', 'takeheart', 'psychicterrain'].includes(id)) { return true; }
			break;
		case 'Umbreon':
			if (['grassknot', 'payday', 'extremespeed', 'boomburst', 'memento', 'nightdaze', 'beatup', 'moonblast', 'baddybad', 'partingshot', 'darkvoid', 'ruination', 'nastyplot', 'trickroom'].includes(id)) { return true; }
			break;
		case 'Leafeon':
			if (['moonlight', 'payday', 'extremespeed', 'boomburst', 'absorb', 'megadrain', 'nightslash', 'sleeppowder', 'gigadrain', 'sappyseed', 'uturn', 'flowertrick', 'chloroblast'].includes(id)) { return true; }
			break;
		case 'Glaceon':
			if (['grassknot', 'moonlight', 'payday', 'extremespeed', 'boomburst', 'iceburn', 'fakeout', 'icespinner', 'freezeshock', 'glaciate', 'freezyfrost', 'chillyreception', 'sheercold'].includes(id)) { return true; }
			break;
		case 'Sylveon':
			if (['grassknot', 'moonlight', 'payday', 'extremespeed', 'boomburst', 'spiritbreak', 'sweetkiss', 'fairylock', 'strangesteam', 'mistyexplosion', 'sparklyswirl', 'teleport', 'springtidestorm', 'naturesmadness', 'decorate'].includes(id)) { return true; }
			break;
		case 'Porygon':
		case 'Porygon2':
		case 'Porygon-Z':
			if (['bodyslam', 'mirrorshot', 'metalburst', 'payday', 'flashcannon', 'voltswitch', 'electricterrain'].includes(id)) { return true; }
			break;
		case 'Kabutops':
			if (['aquacutter', 'nightslash', 'crosspoison', 'flipturn'].includes(id)) { return true; }
			break;
		case 'Omastar':
			if (['aquajet', 'powergem', 'octazooka', 'muddywater', 'flipturn'].includes(id)) { return true; }
			break;
		case 'Aerodactyl':
		case 'Aerodactyl-Mega':
			if (['bravebird', 'uturn', 'headsmash'].includes(id)) { return true; }
			break;
		case 'Armaldo':
			if (['accelerock', 'rockwrecker', 'crosspoison'].includes(id)) { return true; }
			break;
		case 'Cradily':
			if (['hornleech', 'accelerock', 'rockwrecker', 'woodhammer'].includes(id)) { return true; }
			break;
		case 'Rampardos':
			if (['headlongrush', 'behemothbash', 'accelerock', 'wildcharge'].includes(id)) { return true; }
			break;
		case 'Bastiodon':
			if (['shoreup'].includes(id)) { return true; }
			break;
		case 'Carracosta':
			if (['accelerock', 'rockwrecker'].includes(id)) { return true; }
			break;
		case 'Archeops':
			if (['hurricane'].includes(id)) { return true; }
			break;
		case 'Amaura':
		case 'Aurorus':
			if (['boomburst', 'magnetrise', 'accelerock'].includes(id)) { return true; }
			break;
		case 'Tyrantrum':
			if (['poisonfang', 'fissure'].includes(id)) { return true; }
			break;
		case 'Arctozolt':
			if (['iceburn', 'nuzzle', 'zingzap', 'freezeshock', 'supercellslam', 'mountaingale', 'meteorbeam', 'voltswitch', 'sheercold', 'snowscape'].includes(id)) { return true; }
			break;
		case 'Arctovish':
			if (['clamp', 'muddywater', 'freezeshock', 'mountaingale', 'mirrorcoat', 'psychicfangs', 'originpulse', 'meteorbeam', 'flipturn', 'sheercold'].includes(id)) { return true; }
			break;
		case 'Dracozolt':
			if (['nuzzle', 'zingzap', 'clangingscales', 'orderup', 'supercellslam', 'dragonenergy', 'dragondance'].includes(id)) { return true; }
			break;
		case 'Dracovish':
			if (['clamp', 'muddywater', 'clangingscales', 'orderup', 'originpulse', 'meteorbeam', 'flipturn', 'dragonenergy'].includes(id)) { return true; }
			break;
		case 'Munchlax':
		case 'Snorlax':
			if (['submission', 'slackoff'].includes(id)) { return true; }
			break;
		case 'Dragonite':
		case 'Dragonite-Mega':
			if (['dragonascent'].includes(id)) { return true; }
			break;
		case 'Bayleef':
		case 'Meganium':
		case 'Meganium-Mega':
			if (['drainingkiss', 'dreameater', 'junglehealing', 'moonblast', 'wish', 'sparklyswirl', 'strengthsap', 'mistyterrain'].includes(id)) { return true; }
			break;
		case 'Typhlosion':
			if (['mysticalfire', 'earthpower', 'searingshot', 'fissure', 'meteorbeam', 'stealthrock'].includes(id)) { return true; }
			break;
		case 'Typhlosion-Hisui':
			if (['moonblast', 'meteorbeam', 'moongeistbeam', 'stealthrock'].includes(id)) { return true; }
			break;
		case 'Feraligatr':
		case 'Feraligatr-Mega':
			if (['pursuit', 'wavecrash', 'jawlock', 'fishiousrend'].includes(id)) { return true; }
			break;
		case 'Furret':
			if (['fakeout', 'rockblast', 'powertrip', 'return', 'extremespeed'].includes(id)) { return true; }
			break;
		case 'Noctowl':
			if (['pollenpuff', 'bleakwindstorm'].includes(id)) { return true; }
			break;
		case 'Ledian':
			if (['firstimpression', 'closecombat', 'bravebird', 'tailglow'].includes(id)) { return true; }
			break;
		case 'Spinarak':
			if (['firefang', 'destinybond', 'crosspoison', 'firstimpression', 'direclaw'].includes(id)) { return true; }
			break;
		case 'Ariados':
			if (['superfang', 'gunkshot', 'destinybond', 'crosspoison', 'direclaw'].includes(id)) { return true; }
			break;
		case 'Lanturn':
			if (['paraboliccharge', 'lifedew', 'nuzzle', 'energyball', 'wish'].includes(id)) { return true; }
			break;
		case 'Togetic':
		case 'Togekiss':
			if (['moonblast', 'hurricane'].includes(id)) { return true; }
			break;
		case 'Ampharos':
		case 'Ampharos-Mega':
			if (['nuzzle', 'bodypress', 'dracometeor', 'tailglow'].includes(id)) { return true; }
			break;
		case 'Azumarill':
			if (['spiritbreak', 'heartswap', 'moonlight', 'wavecrash'].includes(id)) { return true; }
			break;
		case 'Xatu':
			if (['doomdesire', 'hypnosis', 'icywind', 'mysticalpower', 'cosmicpower'].includes(id)) { return true; }
			break;
		case 'Jumpluff':
			if (['aircutter', 'defog', 'floralhealing', 'bleakwindstorm', 'growth', 'encore', 'spore'].includes(id)) { return true; }
			break;
		case 'Sudowoodo-Disguise':
		case 'Sudowoodo-Busted':
		case 'Sudowoodo':
			if (['rockwrecker', 'accelerock', 'swordsdance'].includes(id)) { return true; }
			break;
		case 'Ambipom':
			if (['dualchop', 'cometpunch', 'armthrust', 'bulletseed', 'doubleironbash'].includes(id)) { return true; }
			break;
		case 'Sunflora':
			if (['ancientpower', 'flamethrower', 'nastyplot', 'frenzyplant'].includes(id)) { return true; }
			break;
		case 'Yanmega':
			if (['hurricane', 'pollenpuff', 'buzzybuzz'].includes(id)) { return true; }
			break;
		case 'Quagsire':
			if (['rockclimb', 'shoreup'].includes(id)) { return true; }
			break;
		case 'Clodsire':
			if (['rockclimb', 'soak'].includes(id)) { return true; }
			break;
		case 'Murkrow':
		case 'Honchkrow':
			if (['trickortreat', 'nightdaze', 'willowisp', 'chatter', 'swordsdance'].includes(id)) { return true; }
			break;
		case 'Misdreavus':
		case 'Mismagius':
			if (['moonblast', 'darkvoid'].includes(id)) { return true; }
			break;
		case 'Forretress':
			if (['steelroller', 'metalburst', 'spinout', 'toxic'].includes(id)) { return true; }
			break;
		case 'Wobbuffet':
			if (['confusion'].includes(id)) { return true; }
			break;
		case 'Dunsparce':
		case 'Dudunsparce':
		case 'Dudunsparce-Three-Segment':
			if (['poisontail', 'chatter', 'firelash', 'nastyplot'].includes(id)) { return true; }
			break;
		case 'Heracross':
		case 'Heracross-Mega':
			if (['horndrill'].includes(id)) { return true; }
			break;
		case 'Weavile':
			if (['iceburn', 'copycat'].includes(id)) { return true; }
			break;
		case 'Sneasel-Hisui':
		case 'Sneasler':
			if (['drainpunch'].includes(id)) { return true; }
			break;
		case 'Granbull':
			if (['spiritbreak', 'return', 'jawlock'].includes(id)) { return true; }
			break;
		case 'Qwilfish':
			if (['barbbarrage'].includes(id)) { return true; }
			break;
		case 'Overqwil':
			if (['scald', 'spinout', 'banefulbunker'].includes(id)) { return true; }
			break;
		case 'Shuckle':
			if (['toxic', 'rockwrecker', 'bodypress'].includes(id)) { return true; }
			break;
		case 'Ursaring':
		case 'Ursaluna':
			if (['rockclimb', 'lunardance', 'moonblast', 'stealthrock'].includes(id)) { return true; }
			break;
		case 'Ursaluna-Bloodmoon':
			if (['rockclimb', 'lunardance', 'stealthrock'].includes(id)) { return true; }
			break;
		case 'Magcargo':
			if (['accelerock', 'fissure', 'burnup', 'scald'].includes(id)) { return true; }
			break;
		case 'Piloswine':
		case 'Mamoswine':
			if (['fissure', 'wildcharge', 'headlongrush', 'sheercold'].includes(id)) { return true; }
			break;
		case 'Corsola':
			if (['soak', 'trickroom', 'strengthsap', 'destinybond', 'spikes', 'flipturn', 'diamondstorm', 'saltcure', 'toxicspikes'].includes(id)) { return true; }
			break;
		case 'Cursola':
		case 'Corsola-Galar':
			if (['flipturn', 'trickroom'].includes(id)) { return true; }
			break;
		case 'Octillery':
			if (['sludgebomb', 'aurasphere'].includes(id)) { return true; }
			break;
		case 'Mantine':
			if (['flipturn', 'bouncybubble'].includes(id)) { return true; }
			break;
		case 'Skarmory':
		case 'Skarmory-Mega':
			if (['guillotine'].includes(id)) { return true; }
			break;
		case 'Houndoom':
		case 'Houndoom-Mega':
			if (['burnup', 'partingshot', 'searingshot', 'fierywrath', 'darkvoid'].includes(id)) { return true; }
			break;
		case 'Donphan':
			if (['fissure', 'hornleech', 'milkdrink'].includes(id)) { return true; }
			break;
		case 'Wyrdeer':
			if (['hornleech', 'wildcharge', 'bulkup'].includes(id)) { return true; }
			break;
		case 'Miltank':
			if (['gunkshot', 'lovelykiss'].includes(id)) { return true; }
			break;
		case 'Pupitar':
		case 'Tyranitar':
		case 'Tyranitar-Mega':
			if (['fissure', 'suckerpunch'].includes(id)) { return true; }
			break;
		case 'Sceptile':
		case 'Sceptile-Mega':
			if (['dracometeor', 'tropkick', 'dragondance'].includes(id)) { return true; }
			break;
		case 'Combusken':
		case 'Blaziken':
		case 'Blaziken-Mega':
			if (['triplekick', 'submission', 'weatherball'].includes(id)) { return true; }
			break;
		case 'Marshtomp':
		case 'Swampert':
		case 'Swampert-Mega':
			if (['icehammer', 'lifedew'].includes(id)) { return true; }
			break;
		case 'Linoone':
			if (['stuffcheeks', 'gunkshot'].includes(id)) { return true; }
			break;
		case 'Obstagoon':
			if (['frustration', 'suckerpunch'].includes(id)) { return true; }
			break;
		case 'Beautifly':
			if (['pollenpuff', 'strengthsap', 'oblivionwing', 'hurricane'].includes(id)) { return true; }
			break;
		case 'Dustox':
			if (['teleport', 'banefulbunker', 'esperwing'].includes(id)) { return true; }
			break;
		case 'Mightyena':
			if (['darkvoid', 'nastyplot'].includes(id)) { return true; }
			break;
		case 'Ludicolo':
			if (['revelationdance', 'aquastep'].includes(id)) { return true; }
			break;
		case 'Swellow':
			if (['floatyfall', 'volttackle'].includes(id)) { return true; }
			break;
		case 'Pelipper':
			if (['muddywater', 'thunder', 'flipturn'].includes(id)) { return true; }
			break;
		case 'Gardevoir':
		case 'Gardevoir-Mega':
			if (['icywind'].includes(id)) { return true; }
			break;
		case 'Gallade':
		case 'Gallade-Mega':
			if (['aquacutter'].includes(id)) { return true; }
			break;
		case 'Surskit':
		case 'Masquerain':
			if (['icebeam', 'fellstinger', 'blizzard'].includes(id)) { return true; }
			break;
		case 'Breloom':
			if (['poisontail', 'armthrust', 'gunkshot'].includes(id)) { return true; }
			break;
		case 'Vigoroth':
		case 'Slaking':
			if (['return'].includes(id)) { return true; }
			break;
		case 'Ninjask':
			if (['finalgambit', 'fellstinger'].includes(id)) { return true; }
			break;
		case 'Loudred':
		case 'Exploud':
			if (['chatter', 'roaroftime', 'partingshot'].includes(id)) { return true; }
			break;
		case 'Hariyama':
			if (['wildcharge'].includes(id)) { return true; }
			break;
		case 'Delcatty':
			if (['cosmicpower'].includes(id)) { return true; }
			break;
		case 'Probopass':
			if (['doomdesire', 'steelbeam', 'headsmash'].includes(id)) { return true; }
			break;
		case 'Mawile':
		case 'Mawile-Mega':
			if (['meteormash'].includes(id)) { return true; }
			break;
		case 'Lairon':
		case 'Aggron':
		case 'Aggron-Mega':
			if (['meteormash', 'accelerock'].includes(id)) { return true; }
			break;
		case 'Medicham':
		case 'Medicham-Mega':
			if (['tropkick', 'psyshieldbash'].includes(id)) { return true; }
			break;
		case 'Manectric':
		case 'Manectric-Mega':
			if (['aurorabeam', 'electrodrift'].includes(id)) { return true; }
			break;
		case 'Plusle':
			if (['moonblast', 'buzzybuzz'].includes(id)) { return true; }
			break;
		case 'Minun':
			if (['icywind', 'buzzybuzz'].includes(id)) { return true; }
			break;
		case 'Volbeat':
			if (['buzzybuzz', 'thunderouskick', 'firstimpression', 'twineedle', 'meteormash', 'boltstrike'].includes(id)) { return true; }
			break;
		case 'Illumise':
			if (['buzzybuzz', 'quiverdance', 'growth'].includes(id)) { return true; }
			break;
		case 'Sableye':
		case 'Sableye-Mega':
			if (['payday'].includes(id)) { return true; }
			break;
		case 'Roserade':
			if (['barbbarrage', 'bittermalice'].includes(id)) { return true; }
			break;
		case 'Swalot':
			if (['stuffcheeks'].includes(id)) { return true; }
			break;
		case 'Sharpedo':
		case 'Sharpedo-Mega':
			if (['wavecrash', 'firefang', 'jawlock'].includes(id)) { return true; }
			break;
		case 'Wailord':
			if (['wavecrash', 'boomburst'].includes(id)) { return true; }
			break;
		case 'Camerupt':
		case 'Camerupt-Mega':
			if (['fissure', 'magmastorm', 'precipiceblades'].includes(id)) { return true; }
			break;
		case 'Torkoal':
			if (['fissure'].includes(id)) { return true; }
			break;
		case 'Grumpig':
			if (['trickortreat', 'slackoff', 'stuffcheeks', 'psychoboost'].includes(id)) { return true; }
			break;
		case 'Spinda':
			if (['headlongrush', 'spinout'].includes(id)) { return true; }
			break;
		case 'Trapinch':
			if (['boomburst', 'fissure'].includes(id)) { return true; }
			break;
		case 'Vibrava':
		case 'Flygon':
			if (['fissure', 'dragondarts'].includes(id)) { return true; }
			break;
		case 'Altaria':
		case 'Altaria-Mega':
			if (['moonblast', 'uturn', 'wish'].includes(id)) { return true; }
			break;
		case 'Cacturne':
			if (['spikyshield', 'fellstinger', 'superpower'].includes(id)) { return true; }
			break;
		case 'Zangoose':
			if (['uturn', 'tidyup'].includes(id)) { return true; }
			break;
		case 'Seviper':
			if (['earthpower', 'focusblast', 'tailglow'].includes(id)) { return true; }
			break;
		case 'Whiscash':
			if (['icefang', 'wavecrash', 'fissure'].includes(id)) { return true; }
			break;
		case 'Solrock':
			if (['accelerock', 'flamecharge', 'flareblitz', 'headsmash'].includes(id)) { return true; }
			break;
		case 'Lunatone':
			if (['accelerock', 'blizzard', 'diamondstorm', 'lusterpurge'].includes(id)) { return true; }
			break;
		case 'Crawdaunt':
			if (['guillotine', 'icehammer'].includes(id)) { return true; }
			break;
		case 'Claydol':
			if (['shoreup'].includes(id)) { return true; }
			break;
		case 'Milotic':
			if (['moonblast', 'agility', 'calmmind'].includes(id)) { return true; }
			break;
		case 'Castform':
			if (['futuresight', 'discharge', 'freezeshock', 'lavaplume', 'scald', 'powergem', 'aircutter', 'scorchingsands', 'airslash', 'surf', 'hypervoice', 'earthquake', 'earthpower', 'doubleedge', 'morningsun', 'freezedry', 'meteorbeam', 'defog', 'voltswitch', 'explosion', 'aeroblast', 'stealthrock', 'boomburst'].includes(id)) { return true; }
			break;
		case 'Kecleon':
			if (['aquajet', 'bulletpunch', 'extremespeed', 'detect'].includes(id)) { return true; }
			break;
		case 'Banette':
		case 'Banette-Mega':
			if (['quickattack', 'brickbreak', 'shadowforce', 'topsyturvy', 'cottonguard'].includes(id)) { return true; }
			break;
		case 'Dusclops':
		case 'Dusknoir':
			if (['drainpunch', 'meteormash', 'spectralthief', 'closecombat'].includes(id)) { return true; }
			break;
		case 'Tropius':
			if (['weatherball', 'curse', 'heatwave'].includes(id)) { return true; }
			break;
		case 'Chimecho':
		case 'Chimecho-Mega':
			if (['craftyshield', 'psystrike'].includes(id)) { return true; }
			break;
		case 'Absol':
		case 'Absol-Mega':
		case 'Absol-Mega-Z':
			if (['crosspoison', 'bitterblade', 'copycat'].includes(id)) { return true; }
			break;
		case 'Glalie':
		case 'Glalie-Mega':
			if (['superfang', 'foulplay', 'iceburn', 'mountaingale', 'sheercold'].includes(id)) { return true; }
			break;
		case 'Froslass':
		case 'Froslass-Mega':
			if (['iceburn', 'sheercold', 'freezedry'].includes(id)) { return true; }
			break;
		case 'Huntail':
			if (['ironhead', 'irontail', 'wavecrash'].includes(id)) { return true; }
			break;
		case 'Gorebyss':
			if (['flashcannon', 'bouncybubble', 'waterspout', 'luminacrash'].includes(id)) { return true; }
			break;
		case 'Sealeo':
		case 'Walrein':
			if (['iceshard', 'sheercold'].includes(id)) { return true; }
			break;
		case 'Relicanth':
			if (['wavecrash', 'accelerock'].includes(id)) { return true; }
			break;
		case 'Luvdisc':
			if (['fishiousrend', 'strengthsap', 'waterspout', 'moonblast', 'flipturn'].includes(id)) { return true; }
			break;
		case 'Metang':
		case 'Metagross':
		case 'Metagross-Mega':
			if (['submission', 'icehammer', 'storedpower', 'crabhammer', 'shiftgear'].includes(id)) { return true; }
			break;
		case 'Grotle':
		case 'Torterra':
			if (['rapidspin', 'gravapple', 'icespinner', 'ragepowder'].includes(id)) { return true; }
			break;
		case 'Monferno':
		case 'Infernape':
			if (['aurasphere', 'pyroball', 'ragefist'].includes(id)) { return true; }
			break;
		case 'Prinplup':
			if (['airslash', 'wavecrash', 'steelbeam', 'freezedry'].includes(id)) { return true; }
			break;
		case 'Empoleon':
			if (['airslash', 'kingsshield', 'wavecrash', 'steelbeam', 'freezedry'].includes(id)) { return true; }
			break;
		case 'Staraptor':
		case 'Staraptor-Mega':
			if (['hyperdrill', 'submission'].includes(id)) { return true; }
			break;
		case 'Bibarel':
			if (['flipturn', 'aquastep', 'woodhammer', 'wavecrash', 'stuffcheeks'].includes(id)) { return true; }
			break;
		case 'Kricketot':
		case 'Kricketune':
			if (['sacredsword', 'boomburst', 'firstimpression', 'fellstinger'].includes(id)) { return true; }
			break;
		case 'Luxray':
			if (['zingzap', 'poisonfang', 'fusionbolt', 'jawlock'].includes(id)) { return true; }
			break;
		case 'Wormadam':
			if (['lunge', 'stealthrock', 'magicalleaf', 'leechseed', 'energyball', 'psychic', 'sappyseed', 'seedflare'].includes(id)) { return true; }
			break;
		case 'Wormadam-Sandy':
			if (['lunge', 'stealthrock', 'bulldoze', 'stompingtantrum', 'wideguard', 'highhorsepower', 'rockslide', 'scorchingsands', 'stoneedge', 'sandsearstorm', 'headlongrush'].includes(id)) { return true; }
			break;
		case 'Wormadam-Trash':
			if (['lunge', 'stealthrock', 'smartstrike', 'ironhead', 'earthpower', 'autotomize', 'spinout', 'meteormash', 'makeitrain'].includes(id)) { return true; }
			break;
		case 'Mothim':
			if (['stickyweb'].includes(id)) { return true; }
			break;
		case 'Vespiquen':
			if (['barbbarrage', 'bleakwindstorm', 'fellstinger', 'stickyweb'].includes(id)) { return true; }
			break;
		case 'Pachirisu':
			if (['buzzybuzz', 'sparklyswirl', 'recover'].includes(id)) { return true; }
			break;
		case 'Floatzel':
			if (['flipturn'].includes(id)) { return true; }
			break;
		case 'Cherrim':
			if (['earthpower', 'mysticalfire'].includes(id)) { return true; }
			break;
		case 'Gastrodon':
			if (['muddywater'].includes(id)) { return true; }
			break;
		case 'Lopunny':
		case 'Lopunny-Mega':
			if (['tropkick'].includes(id)) { return true; }
			break;
		case 'Drifblim':
			if (['selfdestruct', 'infernalparade'].includes(id)) { return true; }
			break;
		case 'Purugly':
			if (['bodypress', 'return', 'milkdrink'].includes(id)) { return true; }
			break;
		case 'Skuntank':
			if (['playrough', 'poisontail', 'partingshot', 'slackoff'].includes(id)) { return true; }
			break;
		case 'Bronzor':
		case 'Bronzong':
			if (['healbell', 'nightshade', 'psyshieldbash', 'chillyreception', 'curse'].includes(id)) { return true; }
			break;
		case 'Chatot':
			if (['oblivionwing', 'esperwing', 'bleakwindstorm', 'torchsong'].includes(id)) { return true; }
			break;
		case 'Spiritomb':
			if (['trickortreat', 'knockoff', 'poltergeist', 'rapidspin', 'astralbarrage'].includes(id)) { return true; }
			break;
		case 'Lucario':
		case 'Lucario-Mega':
		case 'Lucario-Mega-Z':
			if (['bonemerang', 'shadowbone', 'thunderouskick'].includes(id)) { return true; }
			break;
		case 'Gabite':
		case 'Garchomp':
		case 'Garchomp-Mega':
		case 'Garchomp-Mega-Z':
			if (['wildcharge', 'dragondance'].includes(id)) { return true; }
			break;
		case 'Hippopotas':
		case 'Hippowdon':
			if (['liquidation', 'spikes'].includes(id)) { return true; }
			break;
		case 'Drapion':
			if (['suckerpunch', 'barbbarrage', 'crabhammer'].includes(id)) { return true; }
			break;
		case 'Toxicroak':
			if (['aurasphere', 'noxioustorque'].includes(id)) { return true; }
			break;
		case 'Carnivine':
			if (['psychicfangs', 'thunderfang', 'icefang', 'leafblade', 'poisonfang', 'trailblaze', 'earthquake', 'swordsdance'].includes(id)) { return true; }
			break;
		case 'Lumineon':
			if (['sparklingaria', 'bouncybubble', 'hurricane', 'flipturn'].includes(id)) { return true; }
			break;
		case 'Snover':
		case 'Abomasnow':
		case 'Abomasnow-Mega':
			if (['freezeshock', 'mountaingale', 'sheercold', 'iceburn', 'chillyreception'].includes(id)) { return true; }
			break;
		case 'Rotom':
			if (['shadowsneak', 'astralbarrage', 'taunt'].includes(id)) { return true; }
			break;
		case 'Rotom-Heat':
			if (['shadowsneak', 'poltergeist', 'ember', 'incinerate', 'smokescreen', 'firepledge', 'flamethrower', 'blueflare'].includes(id)) { return true; }
			break;
		case 'Rotom-Wash':
			if (['shadowsneak', 'poltergeist', 'watergun', 'waterpulse', 'lifedew', 'waterpledge', 'surf', 'hydropump', 'scald'].includes(id)) { return true; }
			break;
		case 'Rotom-Frost':
			if (['shadowsneak', 'poltergeist', 'powdersnow', 'aurorabeam', 'iceburn', 'icebeam', 'freezedry', 'freezeshock'].includes(id)) { return true; }
			break;
		case 'Rotom-Fan':
			if (['shadowsneak', 'poltergeist', 'gust', 'aircutter', 'tailwind', 'airslash', 'hurricane', 'roost', 'aeroblast'].includes(id)) { return true; }
			break;
		case 'Rotom-Mow':
			if (['shadowsneak', 'poltergeist', 'megadrain', 'magicalleaf', 'leechseed', 'grasspledge', 'energyball', 'leafstorm', 'sappyseed'].includes(id)) { return true; }
			break;
		case 'Servine':
		case 'Serperior':
			if (['curse', 'junglehealing', 'dracometeor'].includes(id)) { return true; }
			break;
		case 'Pignite':
		case 'Emboar':
		case 'Emboar-Mega':
			if (['burnup', 'submission', 'victorydance'].includes(id)) { return true; }
			break;
		case 'Samurott':
			if (['leafblade', 'wavecrash', 'closecombat'].includes(id)) { return true; }
			break;
		case 'Samurott-Hisui':
			if (['leafblade', 'wavecrash', 'closecombat'].includes(id)) { return true; }
			break;
		case 'Stoutland':
			if (['bodyslam', 'frustration', 'return', 'doubleedge', 'closecombat', 'swordsdance'].includes(id)) { return true; }
			break;
		case 'Simisage':
			if (['junglehealing', 'needlearm', 'spikyshield', 'uturn', 'headsmash', 'closecombat', 'gunkshot'].includes(id)) { return true; }
			break;
		case 'Simisear':
			if (['scorchingsands', 'burningbulwark', 'partingshot', 'headsmash', 'closecombat', 'torchsong'].includes(id)) { return true; }
			break;
		case 'Simipour':
			if (['tripleaxel', 'hurricane', 'wavecrash', 'solarbeam', 'flipturn', 'headsmash', 'closecombat', 'gunkshot', 'steameruption'].includes(id)) { return true; }
			break;
		case 'Musharna':
			if (['cosmicpower'].includes(id)) { return true; }
			break;
		case 'Zebstrika':
			if (['flareblitz', 'bulkup'].includes(id)) { return true; }
			break;
		case 'Boldore':
		case 'Gigalith':
			if (['submission', 'rockwrecker'].includes(id)) { return true; }
			break;
		case 'Swoobat':
			if (['esperwing', 'tailglow'].includes(id)) { return true; }
			break;
		case 'Excadrill':
		case 'Excadrill-Mega':
			if (['stoneedge', 'submission', 'horndrill'].includes(id)) { return true; }
			break;
		case 'Audino':
		case 'Audino-Mega':
			if (['moonblast', 'mysticalfire', 'softboiled', 'teleport'].includes(id)) { return true; }
			break;
		case 'Palpitoad':
		case 'Seismitoad':
			if (['muddywater', 'icebeam'].includes(id)) { return true; }
			break;
		case 'Gurdurr':
		case 'Conkeldurr':
			if (['collisioncourse'].includes(id)) { return true; }
			break;
		case 'Leavanny':
			if (['nightslash', 'sacredsword', 'solarblade'].includes(id)) { return true; }
			break;
		case 'Whirlipede':
		case 'Scolipede':
		case 'Scolipede-Mega':
			if (['rockclimb', 'poisontail', 'crosspoison'].includes(id)) { return true; }
			break;
		case 'Cottonee':
		case 'Whimsicott':
			if (['springtidestorm', 'naturesmadness'].includes(id)) { return true; }
			break;
		case 'Basculegion':
		case 'Basculegion-F':
			if (['muddywater', 'superpower', 'shadowbone'].includes(id)) { return true; }
			break;
		case 'Krokorok':
		case 'Krookodile':
			if (['rockpolish'].includes(id)) { return true; }
			break;
		case 'Darmanitan-Galar':
		case 'Darmanitan-Galar-Zen':
			if (['freezeshock', 'iceburn', 'iceshard'].includes(id)) { return true; }
			break;
		case 'Maractus':
			if (['toxicspikes', 'frenzyplant', 'strengthsap'].includes(id)) { return true; }
			break;
		case 'Sigilyph':
			if (['esperwing', 'aurasphere'].includes(id)) { return true; }
			break;
		case 'Cofagrigus':
			if (['bittermalice', 'bonemerang', 'kingsshield', 'recover', 'spectralthief'].includes(id)) { return true; }
			break;
		case 'Runerigus':
			if (['bonemerang', 'kingsshield', 'recover', 'spectralthief'].includes(id)) { return true; }
			break;
		case 'Garbodor':
			if (['flashcannon', 'shellsidearm', 'mortalspin', 'recover', 'direclaw'].includes(id)) { return true; }
			break;
		case 'Zoroark':
			if (['aurasphere', 'fierywrath', 'partingshot'].includes(id)) { return true; }
			break;
		case 'Zoroark-Hisui':
			if (['doubleedge', 'aurasphere', 'moongeistbeam', 'partingshot'].includes(id)) { return true; }
			break;
		case 'Gothita':
		case 'Gothorita':
		case 'Gothitelle':
			if (['psystrike', 'cosmicpower'].includes(id)) { return true; }
			break;
		case 'Cinccino':
			if (['pursuit', 'fakeout', 'aerialace', 'dragontail', 'rocktomb', 'cottonguard'].includes(id)) { return true; }
			break;
		case 'Duosion':
		case 'Reuniclus':
			if (['photongeyser', 'aurasphere'].includes(id)) { return true; }
			break;
		case 'Swanna':
			if (['tripledive', 'waterspout', 'thunder'].includes(id)) { return true; }
			break;
		case 'Vanilluxe':
			if (['iceburn', 'freezeshock', 'recover', 'earthpower'].includes(id)) { return true; }
			break;
		case 'Sawsbuck':
			if (['bodyslam', 'rockslide', 'facade', 'rockclimb'].includes(id)) { return true; }
			break;
		case 'Emolga':
			if (['electrodrift', 'bravebird', 'risingvoltage'].includes(id)) { return true; }
			break;
		case 'Accelgor':
			if (['silktrap', 'tailglow'].includes(id)) { return true; }
			break;
		case 'Escavalier':
			if (['superpower', 'behemothblade', 'twineedle', 'crosspoison', 'autotomize'].includes(id)) { return true; }
			break;
		case 'Amoonguss':
			if (['batonpass'].includes(id)) { return true; }
			break;
		case 'Frillish':
		case 'Jellicent':
			if (['octazooka', 'octolock', 'bouncybubble', 'steameruption'].includes(id)) { return true; }
			break;
		case 'Galvantula':
			if (['swagger', 'risingvoltage'].includes(id)) { return true; }
			break;
		case 'Ferroseed':
		case 'Ferrothorn':
			if (['guillotine', 'needlearm'].includes(id)) { return true; }
			break;
		case 'Klinklang':
			if (['clamp', 'doubleedge', 'metalburst', 'spinout'].includes(id)) { return true; }
			break;
		case 'Eelektrik':
		case 'Eelektross':
		case 'Eelektross-Mega':
			if (['paraboliccharge', 'hydropump', 'nuzzle'].includes(id)) { return true; }
			break;
		case 'Chandelure':
		case 'Chandelure-Mega':
			if (['explosion', 'burnup'].includes(id)) { return true; }
			break;
		case 'Fraxure':
		case 'Haxorus':
			if (['guillotine'].includes(id)) { return true; }
			break;
		case 'Beartic':
			if (['sheercold', 'mountaingale'].includes(id)) { return true; }
			break;
		case 'Cryogonal':
			if (['iceburn', 'freezeshock', 'nastyplot'].includes(id)) { return true; }
			break;
		case 'Stunfisk':
			if (['hydropump', 'voltswitch', 'shoreup', 'fissure'].includes(id)) { return true; }
			break;
		case 'Mienshao':
			if (['blazekick', 'submission'].includes(id)) { return true; }
			break;
		case 'Druddigon':
			if (['poisontail', 'glaiverush'].includes(id)) { return true; }
			break;
		case 'Pawniard':
		case 'Bisharp':
			if (['superpower'].includes(id)) { return true; }
			break;
		case 'Kingambit':
			if (['superpower', 'kingsshield'].includes(id)) { return true; }
			break;
		case 'Vullaby':
		case 'Mandibuzz':
			if (['bonemerang'].includes(id)) { return true; }
			break;
		case 'Braviary':
			if (['temperflare'].includes(id)) { return true; }
			break;
		case 'Braviary-Hisui':
			if (['mistball'].includes(id)) { return true; }
			break;
		case 'Durant':
			if (['smartstrike', 'guillotine', 'uturn', 'closecombat'].includes(id)) { return true; }
			break;
		case 'Heatmor':
			if (['burnup', 'aurasphere'].includes(id)) { return true; }
			break;
		case 'Volcarona':
			if (['burnup', 'scorchingsands'].includes(id)) { return true; }
			break;
		case 'Quilladin':
		case 'Chesnaught':
		case 'Chesnaught-Mega':
			if (['junglehealing', 'needlearm'].includes(id)) { return true; }
			break;
		case 'Diggersby':
			if (['spiritbreak', 'playrough', 'moonlight', 'headlongrush', 'stealthrock'].includes(id)) { return true; }
			break;
		case 'Talonflame':
			if (['solarblade', 'burnup'].includes(id)) { return true; }
			break;
		case 'Pyroar':
		case 'Pyroar-Mega':
			if (['burnup', 'magmastorm'].includes(id)) { return true; }
			break;
		case 'Florges':
			if (['mysticalfire', 'focusblast'].includes(id)) { return true; }
			break;
		case 'Floette-Eternal':
		case 'Floette-Mega':
			if (['mysticalfire', 'focusblast'].includes(id)) { return true; }
			break;
		case 'Gogoat':
			if (['playrough', 'submission'].includes(id)) { return true; }
			break;
		case 'Pangoro':
			if (['suckerpunch', 'wickedblow'].includes(id)) { return true; }
			break;
		case 'Furfrou':
			if (['makeitrain', 'followme', 'return', 'wish'].includes(id)) { return true; }
			break;
		case 'Meowstic':
			if (['psystrike', 'spikes', 'stealthrock', 'taunt', 'toxicspikes'].includes(id)) { return true; }
			break;
		case 'Aegislash':
			if (['behemothbash', 'behemothblade'].includes(id)) { return true; }
			break;
		case 'Aromatisse':
			if (['knockoff', 'toxic', 'softboiled'].includes(id)) { return true; }
			break;
		case 'Swirlix':
		case 'Slurpuff':
			if (['return', 'swordsdance'].includes(id)) { return true; }
			break;
		case 'Malamar':
		case 'Malamar-Mega':
			if (['spinout', 'darkvoid'].includes(id)) { return true; }
			break;
		case 'Dragalge':
		case 'Dragalge-Mega':
			if (['mysticalfire'].includes(id)) { return true; }
			break;
		case 'Clawitzer':
			if (['flipturn', 'octazooka', 'zapcannon', 'originpulse'].includes(id)) { return true; }
			break;
		case 'Heliolisk':
			if (['hydropump'].includes(id)) { return true; }
			break;
		case 'Hawlucha':
		case 'Hawlucha-Mega':
			if (['tailwind'].includes(id)) { return true; }
			break;
		case 'Dedenne':
			if (['toxic', 'fakeout', 'stuffcheeks'].includes(id)) { return true; }
			break;
		case 'Sliggoo':
		case 'Goodra':
			if (['megadrain', 'venoshock', 'recover', 'dragondance'].includes(id)) { return true; }
			break;
		case 'Sliggoo-Hisui':
		case 'Goodra-Hisui':
			if (['megadrain', 'metalclaw', 'mirrorshot', 'venoshock', 'gigadrain', 'scald', 'dragondance'].includes(id)) { return true; }
			break;
		case 'Klefki':
			if (['guillotine', 'moonblast'].includes(id)) { return true; }
			break;
		case 'Trevenant':
			if (['grassyglide', 'gunkshot', 'spiritshackle'].includes(id)) { return true; }
			break;
		case 'Gourgeist':
		case 'Gourgeist-Small':
		case 'Gourgeist-Large':
		case 'Gourgeist-Super':
			if (['mysticalfire', 'strengthsap', 'burnup'].includes(id)) { return true; }
			break;
		case 'Avalugg':
			if (['rockslide', 'gyroball', 'iceburn', 'wavecrash', 'sheercold', 'spikes', 'trickroom'].includes(id)) { return true; }
			break;
		case 'Avalugg-Hisui':
			if (['gyroball', 'iceburn', 'icehammer', 'wavecrash', 'headsmash', 'trickroom'].includes(id)) { return true; }
			break;
		case 'Noivern':
			if (['stompingtantrum', 'bravebird'].includes(id)) { return true; }
			break;
		case 'Dartrix':
		case 'Decidueye':
			if (['strengthsap', 'thousandarrows', 'spectralthief'].includes(id)) { return true; }
			break;
		case 'Decidueye-Hisui':
			if (['bravebird', 'strengthsap', 'thousandarrows', 'closecombat'].includes(id)) { return true; }
			break;
		case 'Torracat':
		case 'Incineroar':
			if (['closecombat', 'coaching'].includes(id)) { return true; }
			break;
		case 'Primarina':
			if (['psychicnoise', 'boomburst', 'torchsong'].includes(id)) { return true; }
			break;
		case 'Toucannon':
			if (['tailslap', 'scaleshot', 'swordsdance'].includes(id)) { return true; }
			break;
		case 'Gumshoos':
			if (['fakeout', 'return', 'suckerpunch'].includes(id)) { return true; }
			break;
		case 'Charjabug':
		case 'Vikavolt':
			if (['overdrive', 'pollenpuff', 'spikes'].includes(id)) { return true; }
			break;
		case 'Oricorio':
		case 'Oricorio-Pom-Pom':
		case 'Oricorio-Pa\u2019u':
		case 'Oricorio-Sensu':
			if (['hex', 'shockwave', 'heatwave', 'shadowball', 'psyshock', 'discharge', 'esperwing', 'razorwind', 'petaldance', 'fierydance', 'raindance'].includes(id)) { return true; }
			break;
		case 'Cutiefly':
		case 'Ribombee':
			if (['defendorder', 'healorder', 'aurasphere'].includes(id)) { return true; }
			break;
		case 'Lycanroc':
			if (['headsmash', 'extremespeed'].includes(id)) { return true; }
			break;
		case 'Lycanroc-Midnight':
			if (['dynamicpunch', 'headsmash', 'jawlock'].includes(id)) { return true; }
			break;
		case 'Lycanroc-Dusk':
			if (['thunderouskick', 'headsmash'].includes(id)) { return true; }
			break;
		case 'Wishiwashi':
			if (['lifedew', 'bulkup'].includes(id)) { return true; }
			break;
		case 'Mareanie':
		case 'Toxapex':
			if (['muddywater', 'barbbarrage', 'flipturn'].includes(id)) { return true; }
			break;
		case 'Mudbray':
		case 'Mudsdale':
			if (['fissure', 'slackoff'].includes(id)) { return true; }
			break;
		case 'Dewpider':
		case 'Araquanid':
			if (['bouncybubble', 'flipturn'].includes(id)) { return true; }
			break;
		case 'Lurantis':
			if (['rockslide', 'crosspoison', 'needlearm', 'hammerarm', 'closecombat'].includes(id)) { return true; }
			break;
		case 'Shiinotic':
			if (['sludgewave', 'toxic', 'floralhealing'].includes(id)) { return true; }
			break;
		case 'Salazzle':
			if (['searingshot', 'burnup', 'malignantchain'].includes(id)) { return true; }
			break;
		case 'Bewear':
			if (['circlethrow', 'slackoff', 'return', 'collisioncourse'].includes(id)) { return true; }
			break;
		case 'Tsareena':
			if (['thunderouskick', 'aquastep'].includes(id)) { return true; }
			break;
		case 'Comfey':
			if (['sparklyswirl'].includes(id)) { return true; }
			break;
		case 'Oranguru':
			if (['submission', 'moonblast', 'takeheart'].includes(id)) { return true; }
			break;
		case 'Passimian':
			if (['sappyseed', 'junglehealing', 'courtchange', 'victorydance'].includes(id)) { return true; }
			break;
		case 'Palossand':
			if (['sandsearstorm', 'strengthsap', 'saltcure'].includes(id)) { return true; }
			break;
		case 'Pyukumuku':
			if (['watergun', 'explosion'].includes(id)) { return true; }
			break;
		case 'Minior':
			if (['lunardance', 'lunarblessing', 'bleakwindstorm', 'dracometeor', 'cosmicpower'].includes(id)) { return true; }
			break;
		case 'Komala':
			if (['closecombat'].includes(id)) { return true; }
			break;
		case 'Turtonator':
			if (['spikes', 'spikyshield', 'burningbulwark'].includes(id)) { return true; }
			break;
		case 'Togedemaru':
			if (['twineedle', 'needlearm'].includes(id)) { return true; }
			break;
		case 'Mimikyu':
			if (['return'].includes(id)) { return true; }
			break;
		case 'Bruxish':
			if (['bugbite', 'clamp', 'jawlock'].includes(id)) { return true; }
			break;
		case 'Drampa':
		case 'Drampa-Mega':
			if (['weatherball'].includes(id)) { return true; }
			break;
		case 'Dhelmise':
			if (['spinout', 'strengthsap', 'shadowsneak'].includes(id)) { return true; }
			break;
		case 'Hakamo-o':
		case 'Kommo-o':
			if (['submission', 'glare'].includes(id)) { return true; }
			break;
		case 'Thwackey':
		case 'Rillaboom':
			if (['suckerpunch', 'closecombat'].includes(id)) { return true; }
			break;
		case 'Raboot':
		case 'Cinderace':
			if (['thunderouskick', 'weatherball'].includes(id)) { return true; }
			break;
		case 'Inteleon':
			if (['aurasphere', 'octazooka', 'sludgebomb', 'zapcannon', 'energyball', 'searingshot', 'nastyplot'].includes(id)) { return true; }
			break;
		case 'Greedent':
			if (['glare', 'headcharge', 'stuffcheeks', 'shedtail'].includes(id)) { return true; }
			break;
		case 'Corviknight':
			if (['acrobatics', 'foulplay'].includes(id)) { return true; }
			break;
		case 'Orbeetle':
			if (['focusblast', 'teleport'].includes(id)) { return true; }
			break;
		case 'Thievul':
			if (['return', 'jawlock'].includes(id)) { return true; }
			break;
		case 'Eldegoss':
			if (['strengthsap', 'earthpower', 'spicyextract', 'chloroblast', 'uturn'].includes(id)) { return true; }
			break;
		case 'Dubwool':
			if (['megakick', 'hyperdrill'].includes(id)) { return true; }
			break;
		case 'Drednaw':
			if (['headsmash'].includes(id)) { return true; }
			break;
		case 'Boltund':
			if (['hyperfang', 'return', 'risingvoltage', 'howl', 'jawlock'].includes(id)) { return true; }
			break;
		case 'Carkol':
		case 'Coalossal':
			if (['magnetrise', 'burnup', 'steameruption', 'eruption'].includes(id)) { return true; }
			break;
		case 'Applin':
			if (['dragontail', 'dragoncheer', 'suckerpunch', 'hypervoice', 'protect', 'solarbeam', 'orderup', 'reflect', 'lightscreen', 'irondefense', 'leafstorm', 'batonpass', 'grassyterrain'].includes(id)) { return true; }
			break;
		case 'Appletun':
			if (['heatwave', 'batonpass'].includes(id)) { return true; }
			break;
		case 'Flapple':
			if (['earthquake', 'batonpass'].includes(id)) { return true; }
			break;
		case 'Dipplin':
			if (['orderup', 'hypervoice', 'lightscreen', 'irondefense'].includes(id)) { return true; }
			break;
		case 'Hydrapple':
			if (['batonpass'].includes(id)) { return true; }
			break;
		case 'Sandaconda':
			if (['recover', 'rockwrecker'].includes(id)) { return true; }
			break;
		case 'Cramorant':
			if (['beakblast', 'snipeshot'].includes(id)) { return true; }
			break;
		case 'Barraskewda':
			if (['wavecrash'].includes(id)) { return true; }
			break;
		case 'Toxtricity':
		case 'Toxtricity-Low-Key':
			if (['partingshot'].includes(id)) { return true; }
			break;
		case 'Centiskorch':
			if (['burnup', 'uturn', 'solarblade', 'firstimpression', 'poisontail', 'healorder'].includes(id)) { return true; }
			break;
		case 'Grapploct':
			if (['suckerpunch', 'fakeout', 'flipturn'].includes(id)) { return true; }
			break;
		case 'Polteageist':
		case 'Polteageist-Antique':
			if (['steameruption'].includes(id)) { return true; }
			break;
		case 'Poltchageist':
		case 'Poltchageist-Artisan':
		case 'Sinistcha':
		case 'Sinistcha-Masterpiece':
			if (['steameruption'].includes(id)) { return true; }
			break;
		case 'Hattrem':
		case 'Hatterene':
			if (['moonblast', 'aurasphere', 'strengthsap'].includes(id)) { return true; }
			break;
		case 'Impidimp':
		case 'Morgrem':
		case 'Grimmsnarl':
			if (['snarl'].includes(id)) { return true; }
			break;
		case 'Falinks':
		case 'Falinks-Mega':
			if (['suckerpunch'].includes(id)) { return true; }
			break;
		case 'Pincurchin':
			if (['voltswitch', 'boltstrike', 'flipturn'].includes(id)) { return true; }
			break;
		case 'Frosmoth':
			if (['dazzlinggleam', 'mirrorcoat', 'ancientpower', 'mudshot'].includes(id)) { return true; }
			break;
		case 'Stonjourner':
			if (['rockwrecker', 'accelerock'].includes(id)) { return true; }
			break;
		case 'Eiscue':
			if (['iceburn', 'headsmash', 'tripleaxel'].includes(id)) { return true; }
			break;
		case 'Indeedee':
			if (['moonblast'].includes(id)) { return true; }
			break;
		case 'Morpeko':
			if (['suckerpunch', 'nastyplot'].includes(id)) { return true; }
			break;
		case 'Copperajah':
			if (['spikes', 'gunkshot'].includes(id)) { return true; }
			break;
		case 'Drakloak':
		case 'Dragapult':
			if (['shadowbone', 'poltergeist'].includes(id)) { return true; }
			break;
		case 'Crocalor':
		case 'Skeledirge':
			if (['psychicnoise', 'partingshot', 'moongeistbeam', 'bittermalice', 'perishsong'].includes(id)) { return true; }
			break;
		case 'Spidops':
			if (['suckerpunch', 'spikyshield'].includes(id)) { return true; }
			break;
		case 'Lokix':
			if (['partingshot'].includes(id)) { return true; }
			break;
		case 'Maushold':
		case 'Maushold-Four':
			if (['softboiled'].includes(id)) { return true; }
			break;
		case 'Dachsbun':
			if (['flamethrower', 'return', 'flareblitz', 'willowisp', 'flamecharge', 'recover', 'detect'].includes(id)) { return true; }
			break;
		case 'Squawkabilly':
		case 'Squawkabilly-Blue':
		case 'Squawkabilly-Yellow':
		case 'Squawkabilly-White':
			if (['vitalthrow'].includes(id)) { return true; }
			break;
		case 'Nacli':
		case 'Naclstack':
		case 'Garganacl':
			if (['fissure', 'rockwrecker', 'knockoff', 'toxic', 'diamondstorm'].includes(id)) { return true; }
			break;
		case 'Armarouge':
			if (['destinybond', 'fakeout'].includes(id)) { return true; }
			break;
		case 'Bellibolt':
			if (['hydropump', 'risingvoltage'].includes(id)) { return true; }
			break;
		case 'Kilowattrel':
			if (['defog', 'oblivionwing', 'boltbeak'].includes(id)) { return true; }
			break;
		case 'Mabosstiff':
			if (['jawlock'].includes(id)) { return true; }
			break;
		case 'Grafaiai':
			if (['poisontail', 'crosspoison', 'toxicspikes'].includes(id)) { return true; }
			break;
		case 'Brambleghast':
			if (['rockslide', 'shadowclaw', 'tailwind', 'swordsdance'].includes(id)) { return true; }
			break;
		case 'Toedscool':
		case 'Toedscruel':
			if (['shoreup', 'skillswap'].includes(id)) { return true; }
			break;
		case 'Klawf':
			if (['rockwrecker', 'icehammer', 'guillotine'].includes(id)) { return true; }
			break;
		case 'Rabsca':
			if (['pollenpuff', 'cosmicpower'].includes(id)) { return true; }
			break;
		case 'Espathra':
			if (['jumpkick', 'vacuumwave'].includes(id)) { return true; }
			break;
		case 'Tinkatuff':
		case 'Tinkaton':
			if (['hammerarm', 'dragonhammer', 'crabhammer', 'return'].includes(id)) { return true; }
			break;
		case 'Bombirdier':
			if (['rockwrecker', 'eggbomb'].includes(id)) { return true; }
			break;
		case 'Palafin':
			if (['submission', 'lifedew'].includes(id)) { return true; }
			break;
		case 'Revavroom':
			if (['wickedtorque', 'blazingtorque', 'uturn', 'geargrind', 'magicaltorque', 'noxioustorque', 'combattorque'].includes(id)) { return true; }
			break;
		case 'Orthworm':
			if (['behemothbash', 'kingsshield', 'batonpass'].includes(id)) { return true; }
			break;
		case 'Houndstone':
			if (['rocktomb', 'stoneedge', 'shadowbone', 'rockwrecker', 'jawlock', 'swordsdance', 'wish'].includes(id)) { return true; }
			break;
		case 'Flamigo':
			if (['reversal', 'wavecrash', 'beakblast'].includes(id)) { return true; }
			break;
		case 'Cetitan':
			if (['submission', 'mountaingale'].includes(id)) { return true; }
			break;
		case 'Veluza':
			if (['psyblade'].includes(id)) { return true; }
			break;
		case 'Dondozo':
			if (['aquajet', 'dragontail', 'flipturn', 'lifedew'].includes(id)) { return true; }
			break;
		case 'Tatsugiri':
			if (['muddywater'].includes(id)) { return true; }
			break;
		case 'Gholdengo':
			if (['payday', 'aurasphere'].includes(id)) { return true; }
			break;
		case 'Baxcalibur':
		case 'Baxcalibur-Mega':
			if (['icehammer', 'mountaingale'].includes(id)) { return true; }
			break;
		case 'Articuno':
			if (['honeclaws', 'calmmind'].includes(id)) { return true; }
			break;
		case 'Articuno-Galar':
			if (['miracleeye', 'defog', 'icywind', 'synchronoise', 'roost', 'amnesia'].includes(id)) { return true; }
			break;
		case 'Zapdos':
			if (['airslash'].includes(id)) { return true; }
			break;
		case 'Zapdos-Galar':
			if (['defog', 'roost'].includes(id)) { return true; }
			break;
		case 'Moltres':
			if (['taunt'].includes(id)) { return true; }
			break;
		case 'Moltres-Galar':
			if (['defog', 'roost', 'willowisp', 'knockoff'].includes(id)) { return true; }
			break;
		case 'Entei':
			if (['earthquake', 'bulkup'].includes(id)) { return true; }
			break;
		case 'Suicune':
			if (['freezeshock', 'hurricane', 'iceburn'].includes(id)) { return true; }
			break;
		case 'Regirock':
			if (['rockwrecker', 'gyroball', 'fissure'].includes(id)) { return true; }
			break;
		case 'Regice':
			if (['iceburn', 'freezeshock', 'freezedry', 'stealthrock', 'chillyreception', 'recover'].includes(id)) { return true; }
			break;
		case 'Registeel':
			if (['payday', 'gyroball'].includes(id)) { return true; }
			break;
		case 'Regieleki':
			if (['quickattack', 'superpower', 'acrobatics', 'magnetrise'].includes(id)) { return true; }
			break;
		case 'Regidrago':
			if (['dragontail', 'ironhead', 'superpower', 'roaroftime'].includes(id)) { return true; }
			break;
		case 'Regigigas':
			if (['bulkup', 'extremespeed'].includes(id)) { return true; }
			break;
		case 'Latias':
		case 'Latias-Mega':
			if (['aquajet'].includes(id)) { return true; }
			break;
		case 'Latios':
		case 'Latios-Mega':
			if (['aquajet'].includes(id)) { return true; }
			break;
		case 'Uxie':
			if (['agility'].includes(id)) { return true; }
			break;
		case 'Mesprit':
			if (['agility'].includes(id)) { return true; }
			break;
		case 'Azelf':
			if (['agility'].includes(id)) { return true; }
			break;
		case 'Heatran':
		case 'Heatran-Mega':
			if (['autotomize'].includes(id)) { return true; }
			break;
		case 'Cobalion':
			if (['finalgambit'].includes(id)) { return true; }
			break;
		case 'Terrakion':
			if (['vacuumwave', 'firstimpression', 'sandstorm'].includes(id)) { return true; }
			break;
		case 'Virizion':
			if (['wildcharge', 'grassyterrain'].includes(id)) { return true; }
			break;
		case 'Keldeo':
			if (['energyball'].includes(id)) { return true; }
			break;
		case 'Tornadus':
		case 'Tornadus-Therian':
			if (['bravebird'].includes(id)) { return true; }
			break;
		case 'Thundurus':
		case 'Thundurus-Therian':
			if (['dualwingbeat', 'airslash'].includes(id)) { return true; }
			break;
		case 'Landorus':
		case 'Landorus-Therian':
			if (['dualwingbeat', 'acrobatics'].includes(id)) { return true; }
			break;
		case 'Enamorus':
		case 'Enamorus-Therian':
			if (['dualwingbeat', 'knockoff', 'defog', 'uturn', 'fleurcannon'].includes(id)) { return true; }
			break;
		case 'Tapu Koko':
			if (['playrough'].includes(id)) { return true; }
			break;
		case 'Tapu Lele':
			if (['imprison', 'teleport'].includes(id)) { return true; }
			break;
		case 'Tapu Bulu':
			if (['grassyglide', 'uturn', 'playrough'].includes(id)) { return true; }
			break;
		case 'Tapu Fini':
			if (['lifedew'].includes(id)) { return true; }
			break;
		case 'Urshifu':
			if (['punishment'].includes(id)) { return true; }
			break;
		case 'Urshifu-Rapid-Strike':
			if (['clamp', 'lifedew', 'hydropump'].includes(id)) { return true; }
			break;
		case 'Spectrier':
		case 'Calyrex-Shadow':
			if (['highhorsepower', 'curse', 'destinybond', 'shadowsneak', 'hypervoice', 'bittermalice'].includes(id)) { return true; }
			break;
		case 'Glastrier':
		case 'Calyrex-Ice':
			if (['iceburn', 'icespinner'].includes(id)) { return true; }
			break;
		case 'Wo-Chien':
			if (['toxic', 'partingshot', 'sleeppowder', 'synthesis'].includes(id)) { return true; }
			break;
		case 'Chien-Pao':
			if (['iceburn', 'clamp', 'beatup', 'foulplay', 'taunt', 'copycat', 'knockoff', 'sheercold'].includes(id)) { return true; }
			break;
		case 'Ting-Lu':
			if (['mudslap', 'sandattack', 'dig', 'gyroball', 'superpower', 'heatcrash'].includes(id)) { return true; }
			break;
		case 'Chi-Yu':
			if (['mysticalfire', 'burnup', 'surf'].includes(id)) { return true; }
			break;
		case 'Okidogi':
			if (['vacuumwave', 'partingshot', 'uturn'].includes(id)) { return true; }
			break;
		case 'Munkidori':
			if (['thunderbolt', 'dazzlinggleam', 'aurasphere'].includes(id)) { return true; }
			break;
		case 'Fezandipiti':
			if (['psychic'].includes(id)) { return true; }
			break;
		case 'Pecharunt':
			if (['shadowsneak', 'darkpulse', 'amnesia', 'soak'].includes(id)) { return true; }
			break;
		case 'Ho-Oh':
			if (['powergem', 'chatter'].includes(id)) { return true; }
			break;
		case 'Kyogre':
		case 'Kyogre-Primal':
			if (['freezeshock'].includes(id)) { return true; }
			break;
		case 'Groudon':
		case 'Groudon-Primal':
			if (['superpower', 'knockoff'].includes(id)) { return true; }
			break;
		case 'Rayquaza':
		case 'Rayquaza-Mega':
			if (['boomburst'].includes(id)) { return true; }
			break;
		case 'Dialga':
		case 'Dialga-Origin':
			if (['gyroball', 'lifedew', 'payday', 'weatherball', 'calmmind'].includes(id)) { return true; }
			break;
		case 'Palkia':
		case 'Palkia-Origin':
			if (['scald', 'weatherball', 'lifedew', 'muddywater', 'hurricane', 'calmmind'].includes(id)) { return true; }
			break;
		case 'Giratina':
		case 'Giratina-Origin':
			if (['disable', 'bodypress', 'heavyslam', 'aurasphere', 'uturn', 'dragondance', 'roost'].includes(id)) { return true; }
			break;
		case 'Reshiram':
			if (['calmmind'].includes(id)) { return true; }
			break;
		case 'Kyurem-White':
		case 'Kyurem-Black':
		case 'Kyurem':
			if (['iceshard'].includes(id)) { return true; }
			break;
		case 'Yveltal':
			if (['pursuit', 'calmmind'].includes(id)) { return true; }
			break;
		case 'Cosmog':
		case 'Cosmoem':
			if (['storedpower', 'explosion'].includes(id)) { return true; }
			break;
		case 'Solgaleo':
			if (['storedpower', 'explosion'].includes(id)) { return true; }
			break;
		case 'Lunala':
			if (['storedpower', 'explosion'].includes(id)) { return true; }
			break;
		case 'Zacian':
		case 'Zacian-Crowned':
			if (['psyblade', 'return'].includes(id)) { return true; }
			break;
		case 'Zamazenta':
		case 'Zamazenta-Crowned':
			if (['return', 'psyshieldbash'].includes(id)) { return true; }
			break;
		case 'Koraidon':
			if (['vacuumwave'].includes(id)) { return true; }
			break;
		case 'Terapagos':
		case 'Terapagos-Terastal':
		case 'Terapagos-Stellar':
			if (['recover'].includes(id)) { return true; }
			break;
		case 'Celebi':
			if (['psychicnoise', 'mysticalfire'].includes(id)) { return true; }
			break;
		case 'Jirachi':
			if (['payday', 'bodypress', 'discharge'].includes(id)) { return true; }
			break;
		case 'Shaymin':
		case 'Shaymin-Sky':
			if (['bleakwindstorm'].includes(id)) { return true; }
			break;
		case 'Deoxys':
		case 'Deoxys-Attack':
		case 'Deoxys-Defense':
		case 'Deoxys-Speed':
			if (['dracometeor', 'swordsdance'].includes(id)) { return true; }
			break;
		case 'Manaphy':
			if (['drainingkiss', 'hurricane', 'thunder'].includes(id)) { return true; }
			break;
		case 'Phione':
			if (['drainingkiss', 'hurricane', 'thunder'].includes(id)) { return true; }
			break;
		case 'Cresselia':
			if (['hex', 'willowisp'].includes(id)) { return true; }
			break;
		case 'Darkrai':
		case 'Darkrai-Mega':
			if (['memento', 'moonblast', 'uturn'].includes(id)) { return true; }
			break;
		case 'Victini':
			if (['burnup', 'victorydance'].includes(id)) { return true; }
			break;
		case 'Meloetta':
			if (['triattack', 'revelationdance', 'boomburst'].includes(id)) { return true; }
			break;
		case 'Genesect':
		case 'Genesect-Douse':
		case 'Genesect-Shock':
		case 'Genesect-Burn':
		case 'Genesect-Chill':
			if (['payday'].includes(id)) { return true; }
			break;
		case 'Hoopa':
		case 'Hoopa-Unbound':
			if (['dazzlinggleam', 'willowisp', 'aurasphere', 'swordsdance'].includes(id)) { return true; }
			break;
		case 'Volcanion':
			if (['icefang', 'wavecrash', 'selfdestruct', 'rapidspin'].includes(id)) { return true; }
			break;
		case 'Silvally':
		case 'Silvally-Bug':
		case 'Silvally-Dark':
		case 'Silvally-Dragon':
		case 'Silvally-Electric':
		case 'Silvally-Fairy':
		case 'Silvally-Fighting':
		case 'Silvally-Fire':
		case 'Silvally-Flying':
		case 'Silvally-Ghost':
		case 'Silvally-Grass':
		case 'Silvally-Ground':
		case 'Silvally-Ice':
		case 'Silvally-Poison':
		case 'Silvally-Psychic':
		case 'Silvally-Rock':
		case 'Silvally-Steel':
		case 'Silvally-Water':
			if (['extremespeed', 'stealthrock', 'recover'].includes(id)) { return true; }
			break;
		case 'Magearna':
		case 'Magearna-Mega':
			if (['payday'].includes(id)) { return true; }
			break;
		case 'Marshadow':
			if (['submission', 'fakeout', 'swordsdance'].includes(id)) { return true; }
			break;
		case 'Zeraora':
		case 'Zeraora-Mega':
			if (['nuzzle', 'copycat', 'thunderclap', 'tripleaxel'].includes(id)) { return true; }
			break;
		case 'Meltan':
		case 'Melmetal':
			if (['curse'].includes(id)) { return true; }
			break;
		case 'Zarude':
			if (['needlearm', 'suckerpunch'].includes(id)) { return true; }
			break;
		case 'Nihilego':
			if (['earthpower'].includes(id)) { return true; }
			break;
		case 'Buzzwole':
			if (['vacuumwave'].includes(id)) { return true; }
			break;
		case 'Pheromosa':
			if (['extremespeed', 'bulkup', 'spikes'].includes(id)) { return true; }
			break;
		case 'Xurkitree':
			if (['reflect', 'triattack'].includes(id)) { return true; }
			break;
		case 'Celesteela':
			if (['payday', 'heatcrash', 'hurricane', 'roost'].includes(id)) { return true; }
			break;
		case 'Kartana':
			if (['guillotine', 'trailblaze', 'autotomize'].includes(id)) { return true; }
			break;
		case 'Guzzlord':
			if (['dragoncheer', 'powerwhip', 'suckerpunch', 'trickroom'].includes(id)) { return true; }
			break;
		case 'Poipole':
			if (['dragoncheer', 'acrobatics', 'aerialace', 'aircutter', 'airslash', 'allyswitch', 'assurance', 'breakingswipe', 'crosspoison', 'darkpulse', 'dracometeor', 'dragonclaw', 'dragondance', 'dragonrush', 'dragontail', 'dualwingbeat', 'flamethrower', 'fireblast', 'fly', 'gigaimpact', 'heatwave', 'hex', 'hyperbeam', 'laserfocus', 'leechlife', 'outrage', 'scaleshot', 'shadowclaw', 'shockwave', 'skyattack', 'skydrop', 'smartstrike', 'snarl', 'spikes', 'swift', 'tailwind', 'thief', 'throatchop', 'thunderbolt', 'uturn', 'xscissor'].includes(id)) { return true; }
			break;
		case 'Naganadel':
			if (['dragoncheer'].includes(id)) { return true; }
			break;
		case 'Blacephalon':
			if (['lavaplume', 'bittermalice', 'shadowsneak', 'burnup'].includes(id)) { return true; }
			break;
		case 'Great Tusk':
			if (['wildcharge'].includes(id)) { return true; }
			break;
		case 'Scream Tail':
			if (['charm', 'shadowball', 'return', 'softboiled', 'batonpass'].includes(id)) { return true; }
			break;
		case 'Brute Bonnet':
			if (['leafblade', 'partingshot', 'knockoff', 'solarblade'].includes(id)) { return true; }
			break;
		case 'Flutter Mane':
			if (['healbell', 'painsplit'].includes(id)) { return true; }
			break;
		case 'Slither Wing':
			if (['defog', 'gigadrain', 'fellstinger'].includes(id)) { return true; }
			break;
		case 'Sandy Shocks':
			if (['signalbeam', 'selfdestruct', 'steelbeam', 'agility'].includes(id)) { return true; }
			break;
		case 'Roaring Moon':
			if (['moonlight'].includes(id)) { return true; }
			break;
		case 'Walking Wake':
			if (['icebeam'].includes(id)) { return true; }
			break;
		case 'Iron Treads':
			if (['headsmash', 'submission'].includes(id)) { return true; }
			break;
		case 'Iron Bundle':
			if (['airslash', 'iceburn', 'hurricane', 'freezeshock', 'freezingglare'].includes(id)) { return true; }
			break;
		case 'Iron Hands':
			if (['counter', 'bulletpunch', 'knockoff', 'thunderwave', 'reversal', 'coaching'].includes(id)) { return true; }
			break;
		case 'Iron Jugulis':
			if (['metalsound', 'earthpower', 'agility', 'workup'].includes(id)) { return true; }
			break;
		case 'Iron Moth':
			if (['quiverdance'].includes(id)) { return true; }
			break;
		case 'Iron Thorns':
			if (['focuspunch', 'aquatail', 'rockwrecker', 'plasmafists'].includes(id)) { return true; }
			break;
		case 'Iron Crown':
			if (['steelroller', 'thunderbolt', 'closecombat', 'psychicterrain'].includes(id)) { return true; }
			break;
		}
		return false;
	}
	private moveIsNotUseless(id: ID, species: Dex.Species, moves: string[], set: Dex.PokemonSet | null) {
		const dex = this.dex;

		let abilityid: ID = set ? toID(set.ability) : '' as ID;
		const itemid: ID = set ? toID(set.item) : '' as ID;

		if (dex.gen === 1) {
			// Usually not useless for Gen 1
			if ([
				'acidarmor', 'amnesia', 'barrier', 'bind', 'blizzard', 'clamp', 'confuseray', 'counter', 'firespin', 'growth', 'headbutt', 'hyperbeam', 'mirrormove', 'pinmissile', 'razorleaf', 'sing', 'slash', 'sludge', 'twineedle', 'wrap',
			].includes(id)) {
				return true;
			}

			// Usually useless for Gen 1
			if ([
				'disable', 'haze', 'leechseed', 'quickattack', 'roar', 'thunder', 'toxic', 'triattack', 'waterfall', 'whirlwind',
			].includes(id)) {
				return false;
			}

			// Not useless only when certain moves aren't present
			switch (id) {
			case 'bubblebeam': return (!moves.includes('surf') && !moves.includes('blizzard'));
			case 'doubleedge': return !moves.includes('bodyslam');
			case 'doublekick': return !moves.includes('submission');
			case 'firepunch': return !moves.includes('fireblast');
			case 'megadrain': return !moves.includes('razorleaf') && !moves.includes('surf');
			case 'megakick': return !moves.includes('hyperbeam');
			case 'reflect': return !moves.includes('barrier') && !moves.includes('acidarmor');
			case 'stomp': return !moves.includes('headbutt');
			case 'submission': return !moves.includes('highjumpkick');
			case 'thunderpunch': return !moves.includes('thunderbolt');
			case 'triattack': return !moves.includes('bodyslam');
			}
			// Useful and Useless moves for Stadium OU, which changes many game mechanics.
			if (this.formatType === 'stadium') {
				if (['doubleedge', 'focusenergy', 'haze'].includes(id)) return true;
				if (['hyperbeam', 'sing', 'hypnosis'].includes(id)) return false;
				switch (id) {
				case 'fly': return !moves.includes('drillpeck');
				case 'dig': return !moves.includes('earthquake');
				}
			}
		}

		if (this.formatType === 'letsgo') {
			if (['megadrain', 'teleport'].includes(id)) return true;
		}

		if (this.formatType === 'metronome') {
			if (id === 'metronome') return true;
		}

		if (itemid === 'pidgeotite') abilityid = 'noguard' as ID;
		if (itemid === 'blastoisinite') abilityid = 'megalauncher' as ID;
		if (itemid === 'aerodactylite') abilityid = 'toughclaws' as ID;
		if (itemid === 'glalitite') abilityid = 'refrigerate' as ID;

		switch (id) {
		case 'fakeout': case 'flamecharge': case 'nuzzle': case 'poweruppunch': case 'trailblaze':
			return abilityid !== 'sheerforce';
		case 'solarbeam': case 'solarblade':
			return ['desolateland', 'drought', 'chlorophyll', 'orichalcumpulse'].includes(abilityid) || itemid === 'powerherb';
		case 'dynamicpunch': case 'grasswhistle': case 'inferno': case 'sing':
			return abilityid === 'noguard';
		case 'heatcrash': case 'heavyslam':
			return species.weightkg >= (species.evos ? 75 : 130);
		case 'aerialace':
			return ['technician', 'toughclaws'].includes(abilityid) && !moves.includes('bravebird');
		case 'ancientpower':
			return ['serenegrace', 'technician'].includes(abilityid) || !moves.includes('powergem');
		case 'aquajet':
			return !moves.includes('jetpunch');
		case 'aurawheel':
			return species.baseSpecies === 'Morpeko';
		case 'axekick':
			return !moves.includes('highjumpkick');
		case 'bellydrum':
			return moves.includes('aquajet') || moves.includes('jetpunch') || moves.includes('extremespeed') ||
				['iceface', 'unburden'].includes(abilityid);
		case 'bulletseed':
			return ['skilllink', 'technician'].includes(abilityid);
		case 'chillingwater':
			return !moves.includes('scald');
		case 'counter':
			return species.baseStats.hp >= 65;
		case 'dazzlinggleam':
			return !moves.includes('alluringvoice') || this.formatType?.includes('doubles');
		case 'darkvoid':
			return dex.gen < 7;
		case 'dualwingbeat':
			return abilityid === 'technician' || !moves.includes('drillpeck');
		case 'electroshot':
			return true;
		case 'feint':
			return abilityid === 'refrigerate';
		case 'futuresight':
			return dex.gen > 5;
		case 'grassyglide':
			return abilityid === 'grassysurge';
		case 'gyroball':
			return species.baseStats.spe <= 60;
		case 'headbutt':
			return abilityid === 'serenegrace';
		case 'hex':
			return !moves.includes('infernalparade');
		case 'hiddenpowerelectric':
			return (dex.gen < 4 && !moves.includes('thunderpunch')) && !moves.includes('thunderbolt');
		case 'hiddenpowerfighting':
			return (dex.gen < 4 && !moves.includes('brickbreak')) && !moves.includes('aurasphere') && !moves.includes('focusblast');
		case 'hiddenpowerfire':
			return (dex.gen < 4 && !moves.includes('firepunch')) && !moves.includes('flamethrower') &&
				!moves.includes('mysticalfire') && !moves.includes('burningjealousy');
		case 'hiddenpowergrass':
			return (dex.gen < 4 && !moves.includes('leafblade')) ||
				(dex.gen > 3 && !moves.includes('energyball') && !moves.includes('grassknot') && !moves.includes('gigadrain'));
		case 'hiddenpowerice':
			return !moves.includes('icebeam') && (dex.gen < 4 && !moves.includes('icepunch')) ||
				(dex.gen > 5 && !moves.includes('aurorabeam') && !moves.includes('glaciate'));
		case 'hiddenpowerflying':
			return dex.gen < 4 && !moves.includes('drillpeck');
		case 'hiddenpowerbug':
			return dex.gen < 4 && !moves.includes('megahorn');
		case 'hiddenpowerpsychic':
			return species.baseSpecies === 'Unown';
		case 'hyperspacefury':
			return species.id === 'hoopaunbound';
		case 'hypnosis':
			return (dex.gen < 4 && !moves.includes('sleeppowder')) || (dex.gen > 6 && abilityid === 'baddreams');
		case 'icepunch':
			return !moves.includes('icespinner') || ['sheerforce', 'ironfist'].includes(abilityid) || itemid === 'punchingglove';
		case 'iciclecrash':
			return !moves.includes('mountaingale');
		case 'iciclespear':
			return dex.gen > 3;
		case 'icywind':
			// Keldeo needs Hidden Power for Electric/Ghost
			return species.baseSpecies === 'Keldeo' || this.formatType === 'doubles';
		case 'infestation':
			return moves.includes('stickyweb');
		case 'irondefense':
			return !moves.includes('acidarmor');
		case 'irontail':
			return dex.gen > 5 && !moves.includes('ironhead') && !moves.includes('gunkshot') && !moves.includes('poisonjab');
		case 'jumpkick':
			return !moves.includes('highjumpkick') && !moves.includes('axekick');
		case 'lastresort':
			return set && set.moves.length < 3;
		case 'leafblade':
			return dex.gen < 4;
		case 'leechlife':
			return dex.gen > 6;
		case 'magiccoat':
			return dex.gen > 3;
		case 'meteorbeam':
			return true;
		case 'mysticalfire':
			return dex.gen > 6 && !moves.includes('flamethrower');
		case 'naturepower':
			return dex.gen === 5;
		case 'needlearm':
			return dex.gen < 4;
		case 'nightslash':
			return !moves.includes('crunch') && !(moves.includes('knockoff') && dex.gen >= 6);
		case 'outrage':
			return dex.gen > 3 && !moves.includes('glaiverush');
		case 'petaldance':
			return abilityid === 'owntempo';
		case 'phantomforce':
			return (!moves.includes('poltergeist') && !moves.includes('shadowclaw')) || this.formatType === 'doubles';
		case 'poisonfang':
			return species.types.includes('Poison') && !moves.includes('gunkshot') && !moves.includes('poisonjab');
		case 'raindance':
			return dex.gen < 4;
		case 'relicsong':
			return species.id === 'meloetta';
		case 'refresh':
			return !moves.includes('aromatherapy') && !moves.includes('healbell');
		case 'risingvoltage':
			return abilityid === 'electricsurge' || abilityid === 'hadronengine';
		case 'rocktomb':
			return abilityid === 'technician';
		case 'selfdestruct':
			return dex.gen < 5 && !moves.includes('explosion');
		case 'shadowpunch':
			return abilityid === 'ironfist' && !moves.includes('ragefist');
		case 'shelter':
			return !moves.includes('acidarmor') && !moves.includes('irondefense');
		case 'skyuppercut':
			return dex.gen < 4;
		case 'smackdown':
			return species.types.includes('Ground');
		case 'smartstrike':
			return species.types.includes('Steel') && !moves.includes('ironhead');
		case 'soak':
			return abilityid === 'unaware';
		case 'steelwing':
			return !moves.includes('ironhead');
		case 'stompingtantrum':
			return (!moves.includes('earthquake') && !moves.includes('drillrun')) || this.formatType === 'doubles';
		case 'stunspore':
			return !moves.includes('thunderwave');
		case 'sunnyday':
			return dex.gen < 4;
		case 'technoblast':
			return dex.gen > 5 && itemid.endsWith('drive') || itemid === 'dousedrive';
		case 'teleport':
			return dex.gen > 7;
		case 'temperflare':
			return (!moves.includes('flareblitz') && !moves.includes('pyroball') && !moves.includes('sacredfire') &&
				!moves.includes('bitterblade') && !moves.includes('firepunch')) || this.formatType === 'doubles';
		case 'terrainpulse': case 'waterpulse':
			return ['megalauncher', 'technician'].includes(abilityid) && !moves.includes('originpulse');
		case 'thief':
			return dex.gen === 2;
		case 'toxicspikes':
			return abilityid !== 'toxicdebris';
		case 'triattack':
			return dex.gen > 3;
		case 'trickroom':
			return species.baseStats.spe <= 100;
		case 'wildcharge':
			return !moves.includes('supercellslam');
		case 'zapcannon':
			return abilityid === 'noguard' || (dex.gen < 4 && !moves.includes('thunderwave'));
		}

		if (this.formatType === 'doubles' && BattleMoveSearch.GOOD_DOUBLES_MOVES.includes(id)) {
			return true;
		}

		const move = dex.moves.get(id);
		if (!move.exists) return true;
		if ((move.status === 'slp' || id === 'yawn') && dex.gen === 9 && !this.formatType) {
			return false;
		}
		if (move.category === 'Status') {
			return BattleMoveSearch.GOOD_STATUS_MOVES.includes(id);
		}
		if (move.basePower < 75) {
			return BattleMoveSearch.GOOD_WEAK_MOVES.includes(id);
		}
		if (id === 'skydrop') return true;
		// strong moves
		if (move.flags['charge']) {
			return itemid === 'powerherb';
		}
		if (move.flags['recharge']) {
			return false;
		}
		if (move.flags['slicing'] && abilityid === 'sharpness') {
			return true;
		}
		return !BattleMoveSearch.BAD_STRONG_MOVES.includes(id);
	}
	static readonly GOOD_STATUS_MOVES = [
		'acidarmor', 'agility', 'aromatherapy', 'auroraveil', 'autotomize', 'banefulbunker', 'batonpass', 'bellydrum', 'bulkup', 'burningbulwark', 'calmmind', 'chillyreception', 'clangoroussoul', 'coil', 'cottonguard', 'courtchange', 'curse', 'defog', 'destinybond', 'detect', 'disable', 'dragondance', 'encore', 'extremeevoboost', 'filletaway', 'geomancy', 'glare', 'haze', 'healbell', 'healingwish', 'healorder', 'heartswap', 'honeclaws', 'kingsshield', 'leechseed', 'lightscreen', 'lovelykiss', 'lunardance', 'magiccoat', 'maxguard', 'memento', 'milkdrink', 'moonlight', 'morningsun', 'nastyplot', 'naturesmadness', 'noretreat', 'obstruct', 'painsplit', 'partingshot', 'perishsong', 'protect', 'quiverdance', 'recover', 'reflect', 'reflecttype', 'rest', 'revivalblessing', 'roar', 'rockpolish', 'roost', 'shedtail', 'shellsmash', 'shiftgear', 'shoreup', 'silktrap', 'slackoff', 'sleeppowder', 'sleeptalk', 'softboiled', 'spikes', 'spikyshield', 'spore', 'stealthrock', 'stickyweb', 'strengthsap', 'substitute', 'switcheroo', 'swordsdance', 'synthesis', 'tailglow', 'tailwind', 'taunt', 'thunderwave', 'tidyup', 'toxic', 'transform', 'trick', 'victorydance', 'whirlwind', 'willowisp', 'wish', 'yawn',
	] as ID[] as readonly ID[];
	static readonly GOOD_WEAK_MOVES = [
		'accelerock', 'acrobatics', 'aquacutter', 'avalanche', 'barbbarrage', 'bonemerang', 'bouncybubble', 'bulletpunch', 'buzzybuzz', 'ceaselessedge', 'circlethrow', 'clearsmog', 'doubleironbash', 'dragondarts', 'dragontail', 'drainingkiss', 'endeavor', 'facade', 'firefang', 'flipturn', 'flowertrick', 'freezedry', 'frustration', 'geargrind', 'gigadrain', 'grassknot', 'gyroball', 'icefang', 'iceshard', 'iciclespear', 'infernalparade', 'knockoff', 'lastrespects', 'lowkick', 'machpunch', 'mortalspin', 'mysticalpower', 'naturesmadness', 'nightshade', 'nuzzle', 'pikapapow', 'populationbomb', 'psychocut', 'psyshieldbash', 'pursuit', 'quickattack', 'ragefist', 'rapidspin', 'return', 'rockblast', 'ruination', 'saltcure', 'scorchingsands', 'seismictoss', 'shadowclaw', 'shadowsneak', 'sizzlyslide', 'stoneaxe', 'storedpower', 'stormthrow', 'suckerpunch', 'superfang', 'surgingstrikes', 'tachyoncutter', 'tailslap', 'thunderclap', 'tripleaxel', 'tripledive', 'twinbeam', 'uturn', 'veeveevolley', 'voltswitch', 'watershuriken', 'weatherball',
	] as ID[] as readonly ID[];
	static readonly BAD_STRONG_MOVES = [
		'belch', 'burnup', 'crushclaw', 'dragonrush', 'dreameater', 'eggbomb', 'firepledge', 'flyingpress', 'futuresight', 'grasspledge', 'hyperbeam', 'hyperfang', 'hyperspacehole', 'jawlock', 'landswrath', 'megakick', 'megapunch', 'mistyexplosion', 'muddywater', 'nightdaze', 'pollenpuff', 'rockclimb', 'selfdestruct', 'shelltrap', 'skyuppercut', 'slam', 'strength', 'submission', 'synchronoise', 'takedown', 'thrash', 'uproar', 'waterpledge',
	] as ID[] as readonly ID[];
	static readonly GOOD_DOUBLES_MOVES = [
		'allyswitch', 'bulldoze', 'coaching', 'electroweb', 'faketears', 'fling', 'followme', 'healpulse', 'helpinghand', 'junglehealing', 'lifedew', 'lunarblessing', 'muddywater', 'pollenpuff', 'psychup', 'ragepowder', 'safeguard', 'skillswap', 'snipeshot', 'wideguard',
	] as ID[] as readonly ID[];
	getBaseResults() {
		if (!this.species) return this.getDefaultResults();
		const dex = this.dex;
		let species = dex.species.get(this.species);
		const format = this.format;
		const isHackmons = (format.includes('hackmons') || format.endsWith('bh'));
		const isSTABmons = (format.includes('stabmons') || format === 'staaabmons');
		const isTradebacks = format.includes('tradebacks');
		const regionBornLegality = dex.gen >= 6 &&
			(/^battle(spot|stadium|festival)/.test(format) || format.startsWith('bss') ||
				format.startsWith('vgc') || (dex.gen === 9 && this.formatType !== 'natdex'));

		let learnsetid = this.firstLearnsetid(species.id);
		let moves: string[] = [];
		let sketchMoves: string[] = [];
		let sketch = false;
		let gen = `${dex.gen}`;
		let lsetTable = BattleTeambuilderTable;
		if (this.formatType?.startsWith('bdsp')) lsetTable = lsetTable['gen8bdsp'];
		if (this.formatType === 'letsgo') lsetTable = lsetTable['gen7letsgo'];
		if (this.formatType === 'bw1') lsetTable = lsetTable['gen5bw1'];
		if (this.formatType?.startsWith('ssdlc1')) lsetTable = lsetTable['gen8dlc1'];
		if (this.formatType?.startsWith('predlc')) lsetTable = lsetTable['gen9predlc'];
		if (this.formatType?.startsWith('svdlc1')) lsetTable = lsetTable['gen9dlc1'];
		while (learnsetid) {
			let learnset = lsetTable.learnsets[learnsetid];
			if (learnset) {
				for (let moveid in learnset) {
					let learnsetEntry = learnset[moveid];
					const move = dex.moves.get(moveid);
					const minGenCode: { [gen: number]: string } = { 6: 'p', 7: 'q', 8: 'g', 9: 'a' };
					if (regionBornLegality && !learnsetEntry.includes(minGenCode[dex.gen])) {
						continue;
					}
					if (
						this.eggMovesOnly(learnsetid, species.id) &&
						(!learnsetEntry.includes('e') || dex.gen !== 9)
					) {
						continue;
					}
					if (
						!learnsetEntry.includes(gen) &&
						(!isTradebacks ? true : !(move.gen <= dex.gen && learnsetEntry.includes(`${dex.gen + 1}`)))
					) {
						continue;
					}
					if (this.formatType !== 'natdex' && move.isNonstandard === "Past") {
						continue;
					}
					if (
						this.formatType?.startsWith('dlc1') &&
						BattleTeambuilderTable['gen8dlc1']?.nonstandardMoves.includes(moveid)
					) {
						continue;
					}
					if (
						this.formatType?.includes('predlc') && this.formatType !== 'predlcnatdex' &&
						BattleTeambuilderTable['gen9predlc']?.nonstandardMoves.includes(moveid)
					) {
						continue;
					}
					if (
						this.formatType?.includes('svdlc1') && this.formatType !== 'svdlc1natdex' &&
						BattleTeambuilderTable['gen9dlc1']?.nonstandardMoves.includes(moveid)
					) {
						continue;
					}
					if (moves.includes(moveid)) continue;
					moves.push(moveid);
					if (moveid === 'sketch') sketch = true;
					if (moveid === 'hiddenpower') {
						moves.push(
							'hiddenpowerbug', 'hiddenpowerdark', 'hiddenpowerdragon', 'hiddenpowerelectric', 'hiddenpowerfighting', 'hiddenpowerfire', 'hiddenpowerflying', 'hiddenpowerghost', 'hiddenpowergrass', 'hiddenpowerground', 'hiddenpowerice', 'hiddenpowerpoison', 'hiddenpowerpsychic', 'hiddenpowerrock', 'hiddenpowersteel', 'hiddenpowerwater'
						);
					}
				}
			}
			learnsetid = this.nextLearnsetid(learnsetid, species.id, true);
		}
		if (sketch || isHackmons) {
			if (isHackmons) moves = [];
			for (let id in BattleMovedex) {
				if (!format.startsWith('cap') && (id === 'paleowave' || id === 'shadowstrike')) continue;
				const move = dex.moves.get(id);
				if (move.gen > dex.gen) continue;
				if (sketch) {
					if (move.flags['nosketch'] || move.isMax || move.isZ) continue;
					if (move.isNonstandard && move.isNonstandard !== 'Past') continue;
					if (move.isNonstandard === 'Past' && this.formatType !== 'natdex') continue;
					sketchMoves.push(move.id);
				} else {
					if (!(dex.gen < 8 || this.formatType === 'natdex') && move.isZ) continue;
					if (typeof move.isMax === 'string') continue;
					if (move.isMax && dex.gen > 8) continue;
					if (move.isNonstandard === 'Past' && this.formatType !== 'natdex') continue;
					if (move.isNonstandard === 'LGPE' && this.formatType !== 'letsgo') continue;
					moves.push(move.id);
				}
			}
		}
		if (this.formatType === 'metronome') moves = ['metronome'];
		if (isSTABmons) {
			for (let id in this.getTable()) {
				const move = dex.moves.get(id);
				if (moves.includes(move.id)) continue;
				if (move.gen > dex.gen) continue;
				if (move.isZ || move.isMax || (move.isNonstandard && move.isNonstandard !== 'Unobtainable')) continue;

				const speciesTypes: string[] = [];
				const moveTypes: string[] = [];
				for (let i = dex.gen; i >= species.gen && i >= move.gen; i--) {
					const genDex = Dex.forGen(i);
					moveTypes.push(genDex.moves.get(move.name).type);

					const pokemon = genDex.species.get(species.name);
					let baseSpecies = genDex.species.get(pokemon.changesFrom || pokemon.name);
					if (!pokemon.battleOnly) speciesTypes.push(...pokemon.types);
					let prevo = pokemon.prevo;
					while (prevo) {
						const prevoSpecies = genDex.species.get(prevo);
						speciesTypes.push(...prevoSpecies.types);
						prevo = prevoSpecies.prevo;
					}
					if (pokemon.battleOnly && typeof pokemon.battleOnly === 'string') {
						species = dex.species.get(pokemon.battleOnly);
					}
					const excludedForme = (s: Dex.Species) => [
						'Alola', 'Alola-Totem', 'Galar', 'Galar-Zen', 'Hisui', 'Paldea', 'Paldea-Combat', 'Paldea-Blaze', 'Paldea-Aqua',
					].includes(s.forme);
					if (baseSpecies.otherFormes && !['Wormadam', 'Urshifu'].includes(baseSpecies.baseSpecies)) {
						if (!excludedForme(species)) speciesTypes.push(...baseSpecies.types);
						for (const formeName of baseSpecies.otherFormes) {
							const forme = dex.species.get(formeName);
							if (!forme.battleOnly && !excludedForme(forme)) speciesTypes.push(...forme.types);
						}
					}
				}
				let valid = false;
				for (let type of moveTypes) {
					if (speciesTypes.includes(type)) {
						valid = true;
						break;
					}
				}
				if (valid) moves.push(id);
			}
		}

		moves.sort();
		sketchMoves.sort();

		if (species.baseSpecies === 'Dugtrio') {
			moves.splice(moves.indexOf('headslongrush'), 1);
		}

		let usableMoves: SearchRow[] = [];
		let uselessMoves: SearchRow[] = [];
		let newMoves: SearchRow[] = [];
		for (const id of moves) {
			const isUsable = this.moveIsNotUseless(id as ID, species, moves, this.set);
			const isNew = this.moveIsNew(id as ID, species);
			if (isUsable) {
				if (!usableMoves.length) usableMoves.push(['header', "Moves"]);
				usableMoves.push(['move', id as ID]);
			} else {
				if (!uselessMoves.length) uselessMoves.push(['header', "Usually useless moves"]);
				uselessMoves.push(['move', id as ID]);
			}
			if (isNew) {
				if (!newMoves.length) newMoves.push(['header', "New Moves"]);
				newMoves.push(['move', id as ID]);
			}
		}
		if (sketchMoves.length) {
			usableMoves.push(['header', "Sketched moves"]);
			uselessMoves.push(['header', "Useless sketched moves"]);
		}
		for (const id of sketchMoves) {
			const isUsable = this.moveIsNotUseless(id as ID, species, sketchMoves, this.set);
			if (isUsable) {
				usableMoves.push(['move', id as ID]);
			} else {
				uselessMoves.push(['move', id as ID]);
			}
		}
		return [...newMoves, ...usableMoves, ...uselessMoves];
	}
	filter(row: SearchRow, filters: string[][]) {
		if (!filters) return true;
		if (row[0] !== 'move') return true;
		const move = this.dex.moves.get(row[1]);
		for (const [filterType, value] of filters) {
			switch (filterType) {
			case 'type':
				if (move.type !== value) return false;
				break;
			case 'category':
				if (move.category !== value) return false;
				break;
			case 'pokemon':
				if (!this.canLearn(value as ID, move.id)) return false;
				break;
			}
		}
		return true;
	}
	sort(results: SearchRow[], sortCol: string, reverseSort?: boolean): SearchRow[] {
		const sortOrder = reverseSort ? -1 : 1;
		switch (sortCol) {
		case 'power':
			let powerTable: { [id: string]: number | undefined } = {
				return: 102, frustration: 102, spitup: 300, trumpcard: 200, naturalgift: 80, grassknot: 120,
				lowkick: 120, gyroball: 150, electroball: 150, flail: 200, reversal: 200, present: 120,
				wringout: 120, crushgrip: 120, heatcrash: 120, heavyslam: 120, fling: 130, magnitude: 150,
				beatup: 24, punishment: 1020, psywave: 1250, nightshade: 1200, seismictoss: 1200,
				dragonrage: 1140, sonicboom: 1120, superfang: 1350, endeavor: 1399, sheercold: 1501,
				fissure: 1500, horndrill: 1500, guillotine: 1500,
			};
			return results.sort(([rowType1, id1], [rowType2, id2]) => {
				let move1 = this.dex.moves.get(id1);
				let move2 = this.dex.moves.get(id2);
				let pow1 = move1.basePower || powerTable[id1] || (move1.category === 'Status' ? -1 : 1400);
				let pow2 = move2.basePower || powerTable[id2] || (move2.category === 'Status' ? -1 : 1400);
				return (pow2 - pow1) * sortOrder;
			});
		case 'accuracy':
			return results.sort(([rowType1, id1], [rowType2, id2]) => {
				let accuracy1 = this.dex.moves.get(id1).accuracy || 0;
				let accuracy2 = this.dex.moves.get(id2).accuracy || 0;
				if (accuracy1 === true) accuracy1 = 101;
				if (accuracy2 === true) accuracy2 = 101;
				return (accuracy2 - accuracy1) * sortOrder;
			});
		case 'pp':
			return results.sort(([rowType1, id1], [rowType2, id2]) => {
				let pp1 = this.dex.moves.get(id1).pp || 0;
				let pp2 = this.dex.moves.get(id2).pp || 0;
				return (pp2 - pp1) * sortOrder;
			});
		case 'name':
			return results.sort(([rowType1, id1], [rowType2, id2]) => {
				const name1 = id1;
				const name2 = id2;
				return (name1 < name2 ? -1 : name1 > name2 ? 1 : 0) * sortOrder;
			});
		}
		throw new Error("invalid sortcol");
	}
}

class BattleCategorySearch extends BattleTypedSearch<'category'> {
	getTable() {
		return { physical: 1, special: 1, status: 1 };
	}
	getDefaultResults(): SearchRow[] {
		return [
			['category', 'physical' as ID],
			['category', 'special' as ID],
			['category', 'status' as ID],
		];
	}
	getBaseResults() {
		return this.getDefaultResults();
	}
	filter(row: SearchRow, filters: string[][]): boolean {
		throw new Error("invalid filter");
	}
	sort(results: SearchRow[], sortCol: string | null, reverseSort?: boolean): SearchRow[] {
		throw new Error("invalid sortcol");
	}
}

class BattleTypeSearch extends BattleTypedSearch<'type'> {
	getTable() {
		return window.BattleTypeChart;
	}
	getDefaultResults(): SearchRow[] {
		const results: SearchRow[] = [];
		for (let id in window.BattleTypeChart) {
			results.push(['type', id as ID]);
		}
		return results;
	}
	getBaseResults() {
		return this.getDefaultResults();
	}
	filter(row: SearchRow, filters: string[][]): boolean {
		throw new Error("invalid filter");
	}
	sort(results: SearchRow[], sortCol: string | null, reverseSort?: boolean): SearchRow[] {
		throw new Error("invalid sortcol");
	}
}
